import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { ordersApi, purchasesApi, metadataApi, artworkJobsApi, partnersApi, financeApi } from '../lib/api';
import { formatDate, formatDateTime, formatCurrency } from '../lib/utils';
import { getInvoiceNet } from '../lib/invoiceMath';
import { Header } from '../components/Header';
import { OrderStatusBadge, PurchaseStatusBadge } from '../components/StatusBadge';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { SupplierConfirmationSection } from '../components/SupplierConfirmationSection';
import { TestPressSupplierHistory } from '../components/TestPressSupplierHistory';
import { QuoteSection } from '../components/QuoteSection';
import { InboundDocumentsSection } from '../components/InboundDocumentsSection';
import { NCBClearanceSection } from '../components/NCBClearanceSection';
import { FinanceSection } from '../components/FinanceSection';
import { TestPressCard } from '../components/TestPressCard';
import OrderSystemEvents from '../components/OrderSystemEvents';
import { ProductionDetailsSection } from '../components/ProductionDetailsSection';
import { UploadLinksSection } from '../components/UploadLinksSection';
import { AdminMasterDiscGroups } from '../components/AdminMasterDiscGroups';
import { AdminMasterFileRow } from '../components/AdminMasterFileRow';
import { RejectMasterFileDialog } from '../components/RejectMasterFileDialog';
import { DemoBadge } from '../components/DemoBadge';
import { AdminNotesCard } from '../components/AdminNotesCard';
import { SonyUploadPanel } from '../components/SonyUploadPanel';
import { 
    CollapsibleSectionsProvider, 
    CollapsibleSectionsControls,
    AdminCollapsibleSection 
} from '../components/AdminCollapsibleSection';
import { deriveRfqStatus, RFQ_STATE } from '../lib/rfq';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '../components/ui/collapsible';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { validateCatalogueNumber } from '../lib/catalogueValidation';
import { 
    ArrowLeft, 
    Plus, 
    MoreHorizontal, 
    Pencil, 
    Trash2,
    Calendar,
    User,
    Mail,
    Phone,
    Disc,
    AlertTriangle,
    Copy,
    Check,
    ChevronDown,
    ChevronRight,
    Music,
    Image,
    Download,
    Clock,
    Package,
    FolderOpen,
    Palette,
    Shield,
    DollarSign,
    ExternalLink,
    Star,
    Building2,
    Inbox,
    Truck,
    FileText,
    Link,
    MessageSquare,
    ShieldCheck,
    RefreshCw,
    Loader2,
    Disc3,
} from 'lucide-react';
import { filesApi } from '../lib/api';
import { Switch } from '../components/ui/switch';

import { API_BASE } from '../lib/apiBase';
// Format order number as 6 digits
const formatOrderNumber = (num) => {
    if (!num) return '------';
    return String(num).padStart(6, '0');
};

// Map a master-file's category to a friendly per-row sub-label for the
// Admin Order Detail "Master" section. Handles legacy vinyl audio_side_a/b
// AND the modern master_slot_<id>[_layerN] categories used by CD/DVD/Bluray
// AND third-party upload-link uploads (same categories).
const getMasterFileSubLabel = (file, order) => {
    const cat = file?.category || '';
    if (cat === 'audio_side_a') return 'Side A';
    if (cat === 'audio_side_b') return 'Side B';
    if (cat === 'audio') return 'Audio';
    if (!cat.startsWith('master_slot_')) return cat || 'File';

    // Extract slot id + optional layer suffix
    // Patterns: master_slot_<uuid> | master_slot_<uuid>_layer0 | _layer1
    const tail = cat.slice('master_slot_'.length);
    const layerMatch = tail.match(/^(.+)_layer(\d)$/);
    const slotId = layerMatch ? layerMatch[1] : tail;
    const layer = layerMatch ? `Layer ${layerMatch[2]}` : null;

    const slots = order?.master_slots || [];
    const idx = slots.findIndex((s) => s.id === slotId);
    const slot = idx >= 0 ? slots[idx] : null;
    // For CD/DVD/Bluray we use index-based positional labels for stability
    // (mirrors the customer-facing MasterFilesCard rendering).
    const positional = ['cd', 'dvd', 'bluray'].includes(order?.product_type);
    let baseLabel;
    if (slot) {
        baseLabel = positional ? `Disc ${idx + 1}` : (slot.label || `Slot ${idx + 1}`);
    } else {
        baseLabel = 'Master';
    }
    return layer ? `${baseLabel} · ${layer}` : baseLabel;
};

// Compact collapsible section component
const CollapsibleSection = ({ title, count, defaultOpen = false, children }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    
    return (
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
            <CollapsibleTrigger asChild>
                <button className="w-full flex items-center justify-between px-3 py-2 bg-background hover:bg-slate-100 rounded text-left transition-colors">
                    <span className="text-xs font-medium uppercase tracking-wider text-slate-600">
                        {title}
                        {count !== undefined && (
                            <span className="ml-2 text-slate-400">({count})</span>
                        )}
                    </span>
                    {isOpen ? (
                        <ChevronDown className="h-4 w-4 text-slate-400" />
                    ) : (
                        <ChevronRight className="h-4 w-4 text-slate-400" />
                    )}
                </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2">
                {children}
            </CollapsibleContent>
        </Collapsible>
    );
};

// Copyable text component
const CopyableText = ({ text, displayText, className = '' }) => {
    const [copied, setCopied] = useState(false);
    
    const handleCopy = () => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    
    return (
        <button 
            onClick={handleCopy}
            className={`flex items-center gap-1 hover:text-slate-700 transition-colors ${className}`}
            title={`Click to copy: ${text}`}
        >
            <span className="font-mono text-xs truncate">{displayText || text}</span>
            {copied ? (
                <Check className="h-3 w-3 text-emerald-500 flex-shrink-0" />
            ) : (
                <Copy className="h-3 w-3 text-slate-400 flex-shrink-0" />
            )}
        </button>
    );
};

export default function OrderDetailPage() {
    const { orderId } = useParams();
    const navigate = useNavigate();
    const { isAdmin } = useAuth();
    
    const [order, setOrder] = useState(null);
    const [purchases, setPurchases] = useState([]);
    const [files, setFiles] = useState([]);
    // Loading flag for the admin "Re-run MD5 verification" action (Master section).
    const [isReVerifying, setIsReVerifying] = useState(false);
    // Loading flag for the admin "Generate missing MD5" action (Master section).
    const [isGeneratingMissingMd5, setIsGeneratingMissingMd5] = useState(false);
    // Loading flag + preview payload for the admin "Generate missing
    // MD5 files" (sidecar) action (Master section). Distinct from
    // ``isGeneratingMissingMd5`` above: that one streams files from S3
    // to compute hashes; this one only writes ``.md5`` sidecars for
    // masters that already have a trusted stored hash.
    const [isGeneratingMd5Sidecars, setIsGeneratingMd5Sidecars] = useState(false);
    const [md5SidecarPlan, setMd5SidecarPlan] = useState(null);
    // Per-file admin-reject modal target (only used on single-disc flat list).
    const [rejectingMasterFile, setRejectingMasterFile] = useState(null);
    const [orderStatuses, setOrderStatuses] = useState([]);
    const [purchaseTypes, setPurchaseTypes] = useState([]);
    const [purchaseStatuses, setPurchaseStatuses] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Finance summary state for sidebar
    const [financeSummary, setFinanceSummary] = useState({ profit: null, margin: null, isEstimate: true });
    
    // Admin S3 files state
    const [adminFiles, setAdminFiles] = useState([]);
    const [copiedPath, setCopiedPath] = useState(null);
    
    // Artwork Job state
    const [artworkJob, setArtworkJob] = useState(null);
    const [reproPartners, setReproPartners] = useState([]);
    const [isCreateArtworkJobDialogOpen, setIsCreateArtworkJobDialogOpen] = useState(false);
    const [isCreatingArtworkJob, setIsCreatingArtworkJob] = useState(false);
    const [artworkJobForm, setArtworkJobForm] = useState({
        partner_id: '',
        instructions: ''
    });
    
    // Dialog states
    const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
    const [isAddPurchaseDialogOpen, setIsAddPurchaseDialogOpen] = useState(false);
    const [isEditPurchaseDialogOpen, setIsEditPurchaseDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [selectedPurchase, setSelectedPurchase] = useState(null);
    
    // Form states
    const [newStatus, setNewStatus] = useState('');
    const [onHoldReasonInput, setOnHoldReasonInput] = useState('');
    const [newPurchase, setNewPurchase] = useState({
        type: '',
        description: '',
        amount: '',
        currency: 'DKK',
        vendor: '',
        supplier_reference: '',
    });
    const [editPurchaseData, setEditPurchaseData] = useState({
        type: '',
        description: '',
        amount: '',
        currency: '',
        status: '',
        vendor: '',
        supplier_reference: '',
    });
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isTogglingSupplierMode, setIsTogglingSupplierMode] = useState(false);

    // Inline edit state for order metadata (admin only)
    const [editingField, setEditingField] = useState(null); // 'title' | 'catalogue_number'
    const [editFieldValue, setEditFieldValue] = useState('');
    const [isSavingField, setIsSavingField] = useState(false);

    // Admin quantity override (manual customer phone request workflow)
    const [isQuantityDialogOpen, setIsQuantityDialogOpen] = useState(false);
    const [quantityForm, setQuantityForm] = useState({
        new_quantity: '',
        propagate_purchases: true,
        propagate_invoice: true,
    });
    const [isSavingQuantity, setIsSavingQuantity] = useState(false);

    useEffect(() => {
        fetchData();
    }, [orderId]);

    // Honour #test-press / #master / #artwork / #supplier hash
    // deep-links so clicks from Admin → Needs Attention land on the
    // right card. Mirrors the handler in OrderFilesPage so both
    // customer-side and admin-side detail pages behave the same.
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const hash = window.location.hash;
        const KNOWN = {
            '#test-press': 'test-press',
            '#master': 'master-files',
            '#audio': 'audio',
            '#artwork': 'artwork',
            '#ncb': 'ncb',
            '#finance': 'finance',
            // Admin → Needs Attention → "Orders awaiting Sony
            // placement" single-order deep-link. The admin-side
            // AdminCollapsibleSection registers under ``id="supplier"``.
            '#supplier': 'supplier',
        };
        const targetId = KNOWN[hash];
        if (!targetId) return;
        const scrollTo = () => {
            const el = document.getElementById(targetId);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                return true;
            }
            return false;
        };
        // Re-scroll across the whole window (not just the first hit): on
        // in-app navigation the target expands/loads its content after the
        // first scroll, growing the page and pushing the section down, so a
        // single early scroll lands short. Keep correcting until the layout
        // settles (~3s) or the element never appears.
        let tries = 0;
        let found = false;
        const timer = setInterval(() => {
            const ok = scrollTo();
            found = found || ok;
            // Stop once we've kept it in view for a few ticks after first
            // find, or after the full window if it never rendered.
            if (++tries >= 20 || (found && tries >= 12)) clearInterval(timer);
        }, 150);
        return () => clearInterval(timer);
    }, [orderId]);

    const fetchData = async () => {
        try {
            // Admin-only endpoints — guard to avoid 403 spam for non-admin users
            const supplierConfPromise = isAdmin
                ? ordersApi.getSupplierConfirmation(orderId).catch(() => ({ data: null }))
                : Promise.resolve({ data: null });
            const adminFilesPromise = isAdmin
                ? filesApi.getAdminFiles(orderId).catch(() => ({ data: [] }))
                : Promise.resolve({ data: [] });

            const [orderRes, purchasesRes, filesRes, statusesRes, typesRes, purchaseStatusesRes, artworkJobRes, reproPartnersRes, supplierConfRes, financeRes, adminFilesRes] = await Promise.all([
                ordersApi.getOne(orderId),
                ordersApi.getPurchases(orderId),
                ordersApi.getFiles(orderId),
                metadataApi.getOrderStatuses(),
                metadataApi.getPurchaseTypes(),
                metadataApi.getPurchaseStatuses(),
                artworkJobsApi.getOrderJob(orderId).catch(() => ({ data: null })),
                partnersApi.list({ type: 'Repro', is_active: true }).catch(() => ({ data: [] })),
                supplierConfPromise,
                financeApi.get(orderId).catch(() => ({ data: null })),
                adminFilesPromise,
            ]);
            
            // Attach supplier confirmation + finance invoice onto the order
            // object so downstream sections (finance accordion header, sales
            // document card, etc.) can render the invoice/booking state
            // without re-fetching. This is authoritative: it comes straight
            // from `finance_invoices` via /orders/{id}/finance.
            const orderWithSupplier = {
                ...orderRes.data,
                supplier_confirmation: supplierConfRes.data,
                invoice: financeRes?.data?.invoice || null,
                invoice_status: financeRes?.data?.invoice?.status ?? orderRes.data.invoice_status ?? null,
                invoice_economic_status: financeRes?.data?.invoice?.economic_status ?? orderRes.data.invoice_economic_status ?? null,
            };
            
            setOrder(orderWithSupplier);
            setPurchases(purchasesRes.data);
            setFiles(filesRes.data);
            setOrderStatuses(statusesRes.data.statuses);
            setPurchaseTypes(typesRes.data.types);
            setPurchaseStatuses(purchaseStatusesRes.data.statuses);
            setNewStatus(orderRes.data.status);
            setArtworkJob(artworkJobRes.data);
            setReproPartners(reproPartnersRes.data);
            setAdminFiles(adminFilesRes.data || []);
            
            // Fetch unified finance summary for sidebar (EUR-based calculations)
            try {
                const summaryRes = await financeApi.getFinanceSummary(orderId);
                if (summaryRes.data) {
                    const summary = summaryRes.data;
                    // Use actual values if available, otherwise estimated
                    const profitEur = summary.actual_profit ?? summary.estimated_profit;
                    const revenueEur = summary.actual_revenue_eur ?? summary.actual_revenue ?? summary.estimated_revenue_eur ?? summary.estimated_revenue;
                    const margin = summary.actual_margin_pct ?? summary.estimated_margin_pct;
                    const hasData = summary.has_production_cost || (revenueEur > 0);
                    const isEstimate = !summary.actual_revenue && !!summary.estimated_revenue;
                    
                    // Get original currency values for display
                    // Use actual if available, otherwise estimated
                    const revenueOriginal = summary.actual_revenue_original ?? summary.estimated_revenue_original;
                    const revenueCurrency = summary.actual_revenue_currency ?? summary.estimated_revenue_currency;
                    const exchangeRate = summary.exchange_rate_used || 1;
                    
                    // Calculate original currency profit if we have original revenue
                    const hasDkkValues = revenueCurrency && revenueCurrency !== 'EUR' && revenueOriginal;
                    const totalCostEur = summary.costs?.total || 0;
                    const profitDkk = hasDkkValues ? revenueOriginal - (totalCostEur * exchangeRate) : null;
                    
                    setFinanceSummary({
                        // EUR values (base currency)
                        profitEur: hasData ? profitEur : null,
                        revenueEur: hasData ? revenueEur : null,
                        margin: hasData ? margin : null,
                        // Original currency values (DKK or other)
                        revenueOriginal: hasDkkValues ? revenueOriginal : null,
                        profitOriginal: hasDkkValues ? profitDkk : null,
                        originalCurrency: revenueCurrency,
                        // Display flags
                        hasDkkValues,
                        isEstimate,
                        // Legacy compatibility
                        profit: hasData ? profitEur : null,
                        currency: 'EUR'
                    });
                }
            } catch (summaryError) {
                // Fallback to legacy calculation if unified summary fails
                if (financeRes.data) {
                    const invoice = financeRes.data.invoice;
                    const purchase = financeRes.data.purchase;
                    const artworkPurchase = financeRes.data.artwork_purchase;
                    const manualPurchases = financeRes.data.manual_purchases || [];
                    
                    const totalCost = (purchase?.total_amount || 0) + (artworkPurchase?.total_amount || 0) + 
                        manualPurchases.reduce((sum, p) => sum + (p.total_amount_eur || p.total_amount || 0), 0);
                    const revenue = getInvoiceNet(invoice) || 0;
                    const profit = revenue - totalCost;
                    const margin = revenue > 0 ? ((profit / revenue) * 100) : 0;
                    const hasData = (invoice?.unit_price > 0) && totalCost > 0;
                    const isEstimate = invoice?.status === 'draft_pending' || invoice?.status === 'draft';
                    
                    setFinanceSummary({
                        profitEur: hasData ? profit : null,
                        revenueEur: hasData ? revenue : null,
                        margin: hasData ? margin : null,
                        revenueOriginal: null,
                        profitOriginal: null,
                        originalCurrency: null,
                        hasDkkValues: false,
                        isEstimate,
                        profit: hasData ? profit : null,
                        currency: 'EUR'
                    });
                }
            }
            
            // Set default partner for artwork job creation
            if (reproPartnersRes.data?.length > 0) {
                const defaultPartner = reproPartnersRes.data.find(p => p.is_default) || reproPartnersRes.data[0];
                setArtworkJobForm(prev => ({ ...prev, partner_id: defaultPartner.id }));
            }
        } catch (error) {
            const status = error?.response?.status;
            if (status === 403) {
                // Access denied: order belongs to a different company or the user lost access.
                // IMPORTANT: do NOT purge auth or logout — this is a resource-level permission issue only.
                toast.error('You do not have access to this order in the current company context');
            } else if (status === 404) {
                toast.error('Order not found — it may have been deleted');
            } else {
                toast.error('Failed to load order details');
            }
            navigate('/dashboard');
        } finally {
            setLoading(false);
        }
    };
    
    const handleCreateArtworkJob = async () => {
        if (!artworkJobForm.partner_id) {
            toast.error('Please select an artwork partner');
            return;
        }
        
        setIsCreatingArtworkJob(true);
        try {
            const response = await artworkJobsApi.create({
                order_id: orderId,
                partner_id: artworkJobForm.partner_id,
                instructions: artworkJobForm.instructions || null
            });
            setArtworkJob(response.data);
            setIsCreateArtworkJobDialogOpen(false);
            toast.success('Artwork job created and assigned');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create artwork job');
        } finally {
            setIsCreatingArtworkJob(false);
        }
    };

    const [isRestoringQuote, setIsRestoringQuote] = useState(false);
    const handleRestoreQuote = async () => {
        setIsRestoringQuote(true);
        try {
            const res = await ordersApi.restoreQuote(orderId);
            toast.success(res.data?.restored === false
                ? 'Order is already in the quote workflow'
                : 'Order restored to the quote workflow');
            await fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to restore quote workflow');
        } finally {
            setIsRestoringQuote(false);
        }
    };

    const handleUpdateStatus = async () => {
        // Placing an order On Hold requires a mandatory reason. We keep
        // the reason field local (``onHoldReasonInput``) and pass it
        // through only when moving TO On Hold; other transitions don't
        // touch the reason.
        const isMovingToHold = newStatus === 'On Hold' && order?.status !== 'On Hold';
        if (isMovingToHold && !onHoldReasonInput.trim()) {
            toast.error('Please enter a reason before placing the order On Hold');
            return;
        }
        setIsSubmitting(true);
        try {
            const payload = { status: newStatus };
            if (isMovingToHold) {
                payload.on_hold_reason = onHoldReasonInput.trim();
            }
            const response = await ordersApi.update(orderId, payload);
            setOrder(response.data);
            setIsStatusDialogOpen(false);
            setOnHoldReasonInput('');
            toast.success('Order status updated');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update status');
        } finally {
            setIsSubmitting(false);
        }
    };

    const openQuantityDialog = () => {
        // Prefer the top-level order.quantity — vinyl_specs.quantity is
        // legacy and can lag behind for non-vinyl products.
        const currentQty = order?.quantity || vinylSpecs?.quantity || 0;
        setQuantityForm({
            new_quantity: String(currentQty || ''),
            propagate_purchases: true,
            propagate_invoice: true,
        });
        setIsQuantityDialogOpen(true);
    };

    const handleAdminUpdateQuantity = async () => {
        const parsed = parseInt(quantityForm.new_quantity, 10);
        if (!parsed || parsed <= 0 || isNaN(parsed)) {
            toast.error('Please enter a valid quantity (positive integer)');
            return;
        }
        const currentQty = order?.quantity || vinylSpecs?.quantity || 0;
        if (parsed === currentQty) {
            toast.info('Quantity unchanged');
            setIsQuantityDialogOpen(false);
            return;
        }

        setIsSavingQuantity(true);
        try {
            const response = await ordersApi.adminUpdateQuantity(orderId, {
                new_quantity: parsed,
                propagate_purchases: quantityForm.propagate_purchases,
                propagate_invoice: quantityForm.propagate_invoice,
            });
            const result = response.data || {};
            const parts = [`Quantity ${result.old_quantity ?? currentQty} → ${result.new_quantity ?? parsed}`];
            if (quantityForm.propagate_purchases) {
                parts.push(`PO lines: ${result.po_lines_updated ?? 0}`);
            }
            if (quantityForm.propagate_invoice) {
                parts.push(`Invoice lines: ${result.invoice_lines_updated ?? 0}`);
            }
            toast.success(parts.join(' · '));

            // Surface any lines that were intentionally NOT touched
            // because they had a manual override or admin edit — the
            // admin should decide what to do with those separately.
            const skippedOverrides = result.po_lines_skipped_overrides ?? 0;
            const skippedManual = result.invoice_lines_skipped_manual ?? 0;
            if (skippedOverrides > 0 || skippedManual > 0) {
                const warnParts = [];
                if (skippedOverrides > 0) warnParts.push(`${skippedOverrides} PO line${skippedOverrides === 1 ? '' : 's'} with manual override`);
                if (skippedManual > 0) warnParts.push(`${skippedManual} manual invoice line${skippedManual === 1 ? '' : 's'}`);
                toast.warning(`Skipped ${warnParts.join(' + ')} — review them manually.`, { duration: 8000 });
            }

            setIsQuantityDialogOpen(false);
            await fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update quantity');
        } finally {
            setIsSavingQuantity(false);
        }
    };

    const handleToggleSupplierMode = async (enabled) => {
        const newMode = enabled ? 'enabled' : 'none';
        const confirmMsg = enabled 
            ? 'Enable supplier workflow? This will require Sony confirmation before invoicing.'
            : 'Disable supplier workflow? This will allow direct invoicing without supplier confirmation.';
        
        if (!window.confirm(confirmMsg)) return;
        
        setIsTogglingSupplierMode(true);
        try {
            const response = await ordersApi.update(orderId, { supplier_mode: newMode });
            setOrder(prev => ({ ...prev, ...response.data }));
            toast.success(enabled ? 'Supplier workflow enabled' : 'Supplier workflow disabled');
            fetchData(); // Refresh to update related sections
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update supplier mode');
        } finally {
            setIsTogglingSupplierMode(false);
        }
    };

    const startEditField = (field) => {
        setEditingField(field);
        if (field === 'title') setEditFieldValue(order.title || '');
        else if (field === 'artist') setEditFieldValue(order.artist || '');
        else setEditFieldValue(order.catalogue_number || order.catalogue_no || '');
    };

    const cancelEditField = () => {
        setEditingField(null);
        setEditFieldValue('');
    };

    const saveEditField = async () => {
        if (!editingField) return;
        const trimmed = editFieldValue.trim();
        if (editingField === 'title' && !trimmed) {
            toast.error('Title cannot be empty');
            return;
        }
        // Catalogue number must pass Sony/Leos format rules (mirrors backend)
        if (editingField === 'catalogue_number') {
            const { value: normalized, error } = validateCatalogueNumber(trimmed);
            if (error) {
                toast.error(error);
                return;
            }
            setIsSavingField(true);
            try {
                await ordersApi.update(orderId, { catalogue_number: normalized });
                setOrder(prev => ({ ...prev, catalogue_number: normalized }));
                toast.success('Catalogue number updated');
                cancelEditField();
            } catch (error) {
                toast.error(error.response?.data?.detail || 'Failed to update');
            } finally {
                setIsSavingField(false);
            }
            return;
        }
        setIsSavingField(true);
        try {
            await ordersApi.update(orderId, { [editingField]: trimmed });
            setOrder(prev => ({ ...prev, [editingField]: trimmed }));
            const labels = { title: 'Title', artist: 'Artist', catalogue_number: 'Catalogue number' };
            toast.success(`${labels[editingField] || 'Field'} updated`);
            cancelEditField();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update');
        } finally {
            setIsSavingField(false);
        }
    };

    const handleAddPurchase = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const response = await purchasesApi.create({
                order_id: orderId,
                type: newPurchase.type,
                description: newPurchase.description,
                amount: parseFloat(newPurchase.amount),
                currency: newPurchase.currency,
                vendor: newPurchase.vendor || null,
                supplier_reference: newPurchase.supplier_reference || null,
            });
            setPurchases([response.data, ...purchases]);
            setIsAddPurchaseDialogOpen(false);
            setNewPurchase({ type: '', description: '', amount: '', currency: 'DKK', vendor: '', supplier_reference: '' });
            toast.success('Purchase added');
        } catch (error) {
            toast.error('Failed to add purchase');
        } finally {
            setIsSubmitting(false);
        }
    };

    const isFinancialLocked = (status) => ['Invoiced', 'Paid'].includes(status);

    const handleEditPurchase = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const updateData = {
                description: editPurchaseData.description,
                status: editPurchaseData.status,
                supplier_reference: editPurchaseData.supplier_reference,
            };
            
            if (!isFinancialLocked(selectedPurchase.status)) {
                updateData.type = editPurchaseData.type;
                updateData.amount = parseFloat(editPurchaseData.amount);
                updateData.currency = editPurchaseData.currency;
                updateData.vendor = editPurchaseData.vendor;
            }
            
            const response = await purchasesApi.update(selectedPurchase.id, updateData);
            setPurchases(purchases.map(p => p.id === selectedPurchase.id ? response.data : p));
            setIsEditPurchaseDialogOpen(false);
            setSelectedPurchase(null);
            toast.success('Purchase updated');
        } catch (error) {
            toast.error('Failed to update purchase');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeletePurchase = async (purchaseId) => {
        if (!window.confirm('Are you sure you want to delete this purchase?')) return;
        try {
            await purchasesApi.delete(purchaseId);
            setPurchases(purchases.filter(p => p.id !== purchaseId));
            toast.success('Purchase deleted');
        } catch (error) {
            toast.error('Failed to delete purchase');
        }
    };

    const handleDeleteOrder = async () => {
        if (!order) return;
        const trimmedText = deleteConfirmText.trim();
        if (trimmedText !== order.title && trimmedText.toUpperCase() !== 'DELETE') {
            toast.error('Please type the order title or "DELETE" to confirm');
            return;
        }
        
        setIsDeleting(true);
        try {
            await ordersApi.delete(orderId, deleteConfirmText);
            toast.success('Order deleted');
            navigate('/dashboard');
        } catch (error) {
            const detail = error.response?.data?.detail;
            toast.error(detail || 'Failed to delete order');
        } finally {
            setIsDeleting(false);
            setIsDeleteDialogOpen(false);
            setDeleteConfirmText('');
        }
    };

    const openEditPurchaseDialog = (purchase) => {
        setSelectedPurchase(purchase);
        setEditPurchaseData({
            type: purchase.type,
            description: purchase.description,
            amount: purchase.amount.toString(),
            currency: purchase.currency,
            status: purchase.status,
            vendor: purchase.vendor || '',
            supplier_reference: purchase.supplier_reference || '',
        });
        setIsEditPurchaseDialogOpen(true);
    };

    const handleDownloadFile = async (file) => {
        try {
            // Orphan S3 objects (no DB record) can't be resolved by /files/{id}/download,
            // so mint a presigned URL directly from the S3 key.
            if (file.source === 's3' && file.s3_key) {
                const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/s3/presigned-download`, {
                    order_id: file.order_id,
                    s3_key: file.s3_key,
                });
                if (res.data?.download_url) {
                    window.open(res.data.download_url, '_blank');
                    return;
                }
            }
            const result = await filesApi.download(file.id);
            // If it's a local file (blob returned), handle download manually
            if (!result.isS3 && result.data) {
                const url = window.URL.createObjectURL(result.data);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', file.original_filename);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            }
            // S3 files are handled by opening in new tab via filesApi.download
        } catch (error) {
            toast.error(error?.response?.data?.detail || 'Failed to download file');
        }
    };

    const totalPurchaseAmount = purchases.reduce((sum, p) => sum + (p.amount || 0), 0);
    const artworkFiles = files.filter(f => f.category === 'artwork');
    const audioFiles = files.filter(f => {
        const cat = f.category || '';
        // Master / audio uploads across every historical shape:
        //  • ``audio`` — single-bucket single-side CD-style upload.
        //  • ``master`` — legacy pre-slots master row.
        //  • ``audio_side_<x>`` — any side letter (a, b, c, d, …). The
        //    older filter only listed ``a`` and ``b`` which under-counted
        //    triple- / quad-sided vinyl and any Side C/D layout, so the
        //    "Customer Upload: N files uploaded" line reported 1 while
        //    the rows below clearly rendered two sides.
        //  • ``master_slot_<uuid>`` and ``master_slot_<uuid>_layer0/1`` —
        //    dynamic slots (all DVD/BD/UHD flows).
        return (
            cat === 'audio' ||
            cat === 'master' ||
            cat.startsWith('audio_side_') ||
            cat.startsWith('master_slot_')
        );
    });
    const tracklistFiles = files.filter(f => f.category === 'tracklist');
    const otherFiles = files.filter(f => f.category === 'other');

    if (loading) {
        return (
            <div className="min-h-screen bg-background">
                <Header />
                <div className="flex items-center justify-center h-96">
                    <LoadingSpinner />
                </div>
            </div>
        );
    }

    if (!order) {
        return (
            <div className="min-h-screen bg-background">
                <Header />
                <div className="flex items-center justify-center h-96">
                    <p className="text-muted-foreground">Order not found</p>
                </div>
            </div>
        );
    }

    const vinylSpecs = order.vinyl_specs || {};

    return (
        <div className="min-h-screen bg-slate-100">
            <Header />
            
            {/* Top bar with back button and actions */}
            <div className="bg-white border-b border-border sticky top-0 z-20">
                <div className="max-w-[1600px] mx-auto px-4 py-2 flex items-center justify-between">
                    <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => navigate('/dashboard')}
                        className="text-slate-600"
                    >
                        <ArrowLeft className="h-4 w-4 mr-1" />
                        Back
                    </Button>
                    
                    {isAdmin && (
                        <div className="flex items-center gap-2">
                            {order.status === 'Draft'
                                && (order.quote_requested_at || order.quote_current_version || (order.quote_versions?.length > 0)) && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleRestoreQuote}
                                    disabled={isRestoringQuote}
                                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                                    data-testid="restore-quote-workflow-button"
                                    title="This order came from the quote workflow. Return it to its quote state instead of submitting it as a normal order."
                                >
                                    <FileText className="h-3 w-3 mr-1" />
                                    {isRestoringQuote ? 'Restoring…' : 'Restore quote workflow'}
                                </Button>
                            )}
                            {order.status === 'Draft' && (
                                <Dialog open={isDeleteDialogOpen} onOpenChange={(open) => {
                                    setIsDeleteDialogOpen(open);
                                    if (!open) setDeleteConfirmText('');
                                }}>
                                    <DialogTrigger asChild>
                                        <Button 
                                            variant="ghost" 
                                            size="sm"
                                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                            data-testid="delete-order-button"
                                        >
                                            <Trash2 className="h-4 w-4 mr-1" />
                                            Delete
                                        </Button>
                                    </DialogTrigger>
                                    <DialogContent className="sm:max-w-md">
                                        <DialogHeader>
                                            <DialogTitle className="flex items-center gap-2 text-red-600">
                                                <AlertTriangle className="h-5 w-5" />
                                                Delete draft order?
                                            </DialogTitle>
                                            <DialogDescription>
                                                This will permanently delete:
                                                <span className="block mt-1 text-sm">
                                                    • {purchases.length} purchase(s), {files.length} file(s), product elements, artwork jobs
                                                </span>
                                                <span className="block mt-2 font-medium text-red-600">Cannot be undone.</span>
                                            </DialogDescription>
                                        </DialogHeader>
                                        <div className="space-y-3 mt-3">
                                            <Label className="text-xs">
                                                Type <code className="bg-slate-100 px-1 rounded">{order.title}</code> or <code className="bg-slate-100 px-1 rounded">DELETE</code>
                                            </Label>
                                            <Input
                                                value={deleteConfirmText}
                                                onChange={(e) => setDeleteConfirmText(e.target.value)}
                                                placeholder="Type to confirm..."
                                                className="font-mono text-sm"
                                                data-testid="delete-confirm-input"
                                            />
                                            <div className="flex justify-end gap-2 pt-2">
                                                <Button variant="outline" size="sm" onClick={() => setIsDeleteDialogOpen(false)}>
                                                    Cancel
                                                </Button>
                                                <Button 
                                                    size="sm"
                                                    variant="destructive"
                                                    onClick={handleDeleteOrder}
                                                    disabled={isDeleting || (deleteConfirmText.trim() !== order.title && deleteConfirmText.trim().toUpperCase() !== 'DELETE')}
                                                    data-testid="confirm-delete-button"
                                                >
                                                    {isDeleting ? 'Deleting...' : 'Delete'}
                                                </Button>
                                            </div>
                                        </div>
                                    </DialogContent>
                                </Dialog>
                            )}
                            
                            <Dialog open={isStatusDialogOpen} onOpenChange={(open) => {
                                setIsStatusDialogOpen(open);
                                if (open) {
                                    // Pre-seed the reason with the current value so
                                    // an admin editing an already-On-Hold order can
                                    // amend the reason rather than re-typing it.
                                    setOnHoldReasonInput(order?.on_hold_reason || '');
                                }
                            }}>
                                <DialogTrigger asChild>
                                    <Button variant="outline" size="sm" data-testid="update-status-button">
                                        <Pencil className="h-3 w-3 mr-1" />
                                        Status
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="sm:max-w-sm">
                                    <DialogHeader>
                                        <DialogTitle>Update Status</DialogTitle>
                                    </DialogHeader>
                                    <div className="space-y-3 mt-2">
                                        <Select value={newStatus} onValueChange={setNewStatus}>
                                            <SelectTrigger data-testid="status-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {orderStatuses.map((status) => (
                                                    <SelectItem key={status} value={status}>{status}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {/* Mandatory reason input when moving TO On Hold.
                                            Rendered inline in the existing modal per the
                                            Feb 2026 requirement: no separate workflow. */}
                                        {newStatus === 'On Hold' && (
                                            <div className="space-y-1">
                                                <Label htmlFor="on-hold-reason" className="text-xs">
                                                    Reason for hold <span className="text-red-500">*</span>
                                                </Label>
                                                <Textarea
                                                    id="on-hold-reason"
                                                    value={onHoldReasonInput}
                                                    onChange={(e) => setOnHoldReasonInput(e.target.value)}
                                                    placeholder="e.g. Awaiting artwork approval from customer"
                                                    rows={3}
                                                    className="text-xs"
                                                    data-testid="on-hold-reason-input"
                                                />
                                                <p className="text-[10px] text-slate-500">
                                                    Preserved in order history when the hold is later released.
                                                </p>
                                            </div>
                                        )}
                                        <div className="flex justify-end gap-2">
                                            <Button variant="outline" size="sm" onClick={() => setIsStatusDialogOpen(false)}>Cancel</Button>
                                            <Button
                                                size="sm"
                                                onClick={handleUpdateStatus}
                                                disabled={
                                                    isSubmitting ||
                                                    (newStatus === 'On Hold' && order?.status !== 'On Hold' && !onHoldReasonInput.trim())
                                                }
                                                data-testid="confirm-status-button"
                                            >
                                                {isSubmitting ? 'Updating...' : 'Update'}
                                            </Button>
                                        </div>
                                    </div>
                                </DialogContent>
                            </Dialog>
                        </div>
                    )}
                </div>
            </div>

            {/* 3-Column Layout */}
            <div className="max-w-[1600px] mx-auto px-4 py-4">
                {/* NOTE: <QuoteSection> was previously rendered here at the
                    top of the admin order page. It now lives inside the
                    Finance accordion (see the "Customer Quotation" sub-
                    section of FinanceSection). The badge on the Finance
                    accordion header surfaces the current quote state
                    while collapsed — do NOT re-add a QuoteSection here
                    or you will get a duplicate quote banner. */}

                <div className="grid grid-cols-12 gap-4">
                    
                    {/* LEFT SIDEBAR - Context/Reference (sticky) */}
                    <div className="col-span-12 lg:col-span-3">
                        <div className="lg:sticky lg:top-16 lg:max-h-[calc(100vh-5rem)] lg:overflow-y-auto space-y-3 pr-1">
                            
                            {/* Order Header - Title, Order #, Catalogue No, Status */}
                            <div className="bg-white rounded-lg p-3 border border-slate-300 shadow-premium-sm">
                                {/* Catalogue Number */}
                                <div className="mb-1.5">
                                    {editingField === 'catalogue_number' ? (
                                        <div className="space-y-1.5">
                                            <Label className="text-[10px] uppercase tracking-wider text-blue-600 font-semibold">Cat. No.</Label>
                                            <Input
                                                value={editFieldValue}
                                                onChange={(e) => setEditFieldValue(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') saveEditField();
                                                    if (e.key === 'Escape') cancelEditField();
                                                }}
                                                autoFocus
                                                placeholder="e.g. OTR-001"
                                                className="h-7 text-sm font-semibold"
                                                data-testid="edit-catalogue_number-input"
                                            />
                                            <div className="flex gap-1.5">
                                                <Button size="sm" className="h-6 text-[11px] px-2" onClick={saveEditField} disabled={isSavingField} data-testid="save-field-btn">
                                                    <Check className="h-3 w-3 mr-1" />Save
                                                </Button>
                                                <Button size="sm" variant="ghost" className="h-6 text-[11px] px-2" onClick={cancelEditField} data-testid="cancel-field-btn">
                                                    Cancel
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            {(order.catalogue_number || order.catalogue_no) ? (
                                                <div className="group flex items-center gap-1">
                                                    <span className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mr-1">Cat.</span>
                                                    <span
                                                        className={`font-semibold text-slate-900 text-sm leading-tight ${isAdmin ? 'cursor-pointer hover:text-blue-600 transition-colors' : ''}`}
                                                        onClick={isAdmin ? () => startEditField('catalogue_number') : undefined}
                                                        title={isAdmin ? 'Click to edit' : undefined}
                                                        data-testid="catalogue-number"
                                                    >
                                                        {order.catalogue_number || order.catalogue_no}
                                                    </span>
                                                    {isAdmin && (
                                                        <button onClick={() => startEditField('catalogue_number')} className="opacity-0 group-hover:opacity-100 transition-opacity" data-testid="edit-catalogue-number-btn">
                                                            <Pencil className="h-3 w-3 text-slate-400 hover:text-blue-500" />
                                                        </button>
                                                    )}
                                                </div>
                                            ) : isAdmin ? (
                                                <button
                                                    onClick={() => startEditField('catalogue_number')}
                                                    className="text-[11px] text-blue-500 hover:text-blue-700 flex items-center gap-1"
                                                    data-testid="add-catalogue-number-btn"
                                                >
                                                    <Plus className="h-3 w-3" /> Add Cat. No.
                                                </button>
                                            ) : null}
                                        </>
                                    )}
                                </div>

                                {/* Title */}
                                <div className="mb-1.5">
                                    {editingField === 'title' ? (
                                        <div className="space-y-1.5">
                                            <Label className="text-[10px] uppercase tracking-wider text-blue-600 font-semibold">Title</Label>
                                            <Input
                                                value={editFieldValue}
                                                onChange={(e) => setEditFieldValue(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') saveEditField();
                                                    if (e.key === 'Escape') cancelEditField();
                                                }}
                                                autoFocus
                                                className="h-7 text-sm font-semibold"
                                                data-testid="edit-title-input"
                                            />
                                            <div className="flex gap-1.5">
                                                <Button size="sm" className="h-6 text-[11px] px-2" onClick={saveEditField} disabled={isSavingField} data-testid="save-field-btn">
                                                    <Check className="h-3 w-3 mr-1" />Save
                                                </Button>
                                                <Button size="sm" variant="ghost" className="h-6 text-[11px] px-2" onClick={cancelEditField} data-testid="cancel-field-btn">
                                                    Cancel
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="group flex items-baseline gap-1.5">
                                            <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Title:</span>
                                            <h1
                                                className={`font-semibold text-slate-900 text-sm leading-tight ${isAdmin ? 'cursor-pointer hover:text-blue-600 transition-colors' : ''}`}
                                                onClick={isAdmin ? () => startEditField('title') : undefined}
                                                title={isAdmin ? 'Click to edit' : undefined}
                                                data-testid="order-title"
                                            >
                                                {order.title}
                                            </h1>
                                            {isAdmin && (
                                                <button onClick={() => startEditField('title')} className="opacity-0 group-hover:opacity-100 transition-opacity" data-testid="edit-title-btn">
                                                    <Pencil className="h-3 w-3 text-slate-400 hover:text-blue-500" />
                                                </button>
                                            )}
                                            <DemoBadge order={order} size="md" className="ml-2" />
                                        </div>
                                    )}
                                </div>

                                {/* Artist (editable; separate from title — see PRD). */}
                                <div className="mb-2">
                                    {editingField === 'artist' ? (
                                        <div className="space-y-1.5">
                                            <Label className="text-[10px] uppercase tracking-wider text-blue-600 font-semibold">Artist</Label>
                                            <Input
                                                value={editFieldValue}
                                                onChange={(e) => setEditFieldValue(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') saveEditField();
                                                    if (e.key === 'Escape') cancelEditField();
                                                }}
                                                autoFocus
                                                className="h-7 text-sm"
                                                data-testid="edit-artist-input"
                                            />
                                            <div className="flex gap-1.5">
                                                <Button size="sm" className="h-6 text-[11px] px-2" onClick={saveEditField} disabled={isSavingField} data-testid="save-field-btn">
                                                    <Check className="h-3 w-3 mr-1" />Save
                                                </Button>
                                                <Button size="sm" variant="ghost" className="h-6 text-[11px] px-2" onClick={cancelEditField} data-testid="cancel-field-btn">
                                                    Cancel
                                                </Button>
                                            </div>
                                        </div>
                                    ) : order.artist ? (
                                        <div className="group flex items-center gap-1" data-testid="order-artist">
                                            <span className="text-xs text-slate-400">Artist:</span>
                                            <span
                                                className={`text-xs font-medium text-slate-700 ${isAdmin ? 'cursor-pointer hover:text-blue-600 transition-colors' : ''}`}
                                                onClick={isAdmin ? () => startEditField('artist') : undefined}
                                                title={isAdmin ? 'Click to edit' : undefined}
                                            >
                                                {order.artist}
                                            </span>
                                            {isAdmin && (
                                                <button onClick={() => startEditField('artist')} className="opacity-0 group-hover:opacity-100 transition-opacity" data-testid="edit-artist-btn">
                                                    <Pencil className="h-3 w-3 text-slate-400 hover:text-blue-500" />
                                                </button>
                                            )}
                                        </div>
                                    ) : isAdmin ? (
                                        <button
                                            onClick={() => startEditField('artist')}
                                            className="text-xs text-slate-400 hover:text-blue-600 italic flex items-center gap-1"
                                            data-testid="add-artist-btn"
                                        >
                                            <Pencil className="h-3 w-3" />
                                            Add artist…
                                        </button>
                                    ) : null}
                                </div>

                                <div className="text-[11px] text-slate-400 mb-2 font-mono">
                                    <CopyableText
                                        text={formatOrderNumber(order.order_number)}
                                        displayText={`#${formatOrderNumber(order.order_number)}`}
                                        className="text-slate-400"
                                    />
                                </div>
                                <OrderStatusBadge
                                    status={order.status}
                                    onHoldReason={order.on_hold_reason}
                                    onHoldAt={order.on_hold_at}
                                    onHoldBy={order.on_hold_by}
                                />
                            </div>
                            
                            {/* Customer / Company */}
                            <div className="bg-white rounded-lg p-3 border border-slate-300 shadow-premium-sm">
                                
                                {/* Company Name */}
                                <div className="text-xs font-semibold text-slate-700 mb-1" data-testid="customer-name">
                                    {order.organization_name || order.customer_name}
                                </div>
                                
                                {/* Contact Person */}
                                {order.customer_name && order.organization_name && (
                                    <div className="flex items-center gap-1.5 text-xs text-slate-600 mb-1">
                                        <User className="h-3 w-3 text-slate-400" />
                                        <span>{order.customer_name}</span>
                                    </div>
                                )}
                                
                                {/* Email */}
                                <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1">
                                    <Mail className="h-3 w-3 text-slate-400" />
                                    <span className="truncate" data-testid="customer-email">{order.customer_email}</span>
                                </div>
                                
                                {/* Phone */}
                                {order.customer_phone && (
                                    <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1">
                                        <Phone className="h-3 w-3 text-slate-400" />
                                        <span>{order.customer_phone}</span>
                                    </div>
                                )}
                                
                                {/* Delivery Address (single or split) */}
                                {(order.delivery_mode === 'split' && order.delivery_addresses?.length) ? (
                                    <div className="mt-2 pt-2 border-t border-slate-100" data-testid="sidebar-split-delivery">
                                        <div className="flex items-center justify-between mb-1.5">
                                            <div className="text-[10px] uppercase text-slate-400">Delivery</div>
                                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-semibold">
                                                Split · {order.delivery_addresses.length} addresses
                                            </span>
                                        </div>
                                        <div className="space-y-2">
                                            {order.delivery_addresses.map((d, i) => (
                                                <div key={d.id || i} className="text-[11px] text-slate-600 leading-tight pb-1.5 border-b border-slate-50 last:border-b-0">
                                                    <div className="flex items-center justify-between">
                                                        <span className="font-semibold text-slate-700">{i + 1}) {d.label || `Delivery ${i + 1}`}</span>
                                                        <span className="text-[10px] px-1 py-0 rounded bg-slate-100 text-slate-600 font-medium">Qty: {d.quantity}</span>
                                                    </div>
                                                    {d.company_name && <div className="text-slate-700">{d.company_name}</div>}
                                                    {d.attention_to && <div className="text-slate-500">Attn: {d.attention_to}</div>}
                                                    <div>{[d.street, d.postal_code && `${d.postal_code} ${d.city}`, d.country].filter(Boolean).join(', ')}</div>
                                                    {d.note && <div className="italic text-slate-400 mt-0.5">"{d.note}"</div>}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : (order.delivery_address || order.shipping_address) && (
                                    <div className="mt-2 pt-2 border-t border-slate-100">
                                        <div className="text-[10px] uppercase text-slate-400 mb-1">Delivery</div>
                                        <div className="text-[11px] text-slate-600 leading-tight">
                                            {(() => {
                                                const addr = order.delivery_address || order.shipping_address;
                                                if (typeof addr === 'string') return addr;
                                                if (typeof addr === 'object') {
                                                    return [addr.street, addr.city, addr.postal_code, addr.country].filter(Boolean).join(', ');
                                                }
                                                return '—';
                                            })()}
                                        </div>
                                    </div>
                                )}
                                
                                {/* Billing Approval Status */}
                                {isAdmin && order.organization_id && (
                                    <div className="mt-2 pt-2 border-t border-slate-100">
                                        {order.organization_billing_status === 'synced_to_economic' ? (
                                            <div className="flex items-center gap-1.5" data-testid="org-approved-badge">
                                                <Check className="h-3 w-3 text-emerald-600" />
                                                <span className="text-[10px] font-medium text-emerald-700">Billing approved</span>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-1.5" data-testid="org-not-approved-badge">
                                                <AlertTriangle className="h-3 w-3 text-amber-500" />
                                                <span className="text-[10px] font-medium text-amber-600">Billing not approved</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            
                            {/* Order Specs - Product Type, Format, Quantity, Delivery */}
                            <div className="bg-white rounded-lg p-3 border border-slate-300 shadow-premium-sm">
                                <div className="flex items-center gap-1.5 text-slate-600 mb-2">
                                    <Package className="h-3 w-3" />
                                    <span className="text-[10px] uppercase font-medium">Order Specs</span>
                                </div>
                                <div className="space-y-1.5 text-xs">
                                    <div className="flex justify-between">
                                        <span className="text-slate-400">Product</span>
                                        <span className="text-slate-700 font-medium" data-testid="order-specs-product">
                                            {(() => {
                                                // Determine product type STRICTLY from product_type /
                                                // media_type — never fall through to vinyl_specs, since
                                                // some CD/DVD/Blu-ray orders still carry legacy
                                                // vinyl_specs remnants that would otherwise mis-label
                                                // them as Vinyl.
                                                const productType = order.product_type || order.media_type;
                                                if (productType === 'vinyl') return 'Vinyl';
                                                if (productType === 'cd') return 'CD';
                                                if (productType === 'dvd') return 'DVD';
                                                if (productType === 'bluray' || productType === 'blu-ray') return 'Blu-ray';
                                                return productType || '—';
                                            })()}
                                        </span>
                                    </div>
                                    {/* Format row — only relevant for vinyl (7" / 10" / 12"). For
                                        disc-based products we surface disc count instead, so the
                                        panel matches what the customer actually ordered. */}
                                    {(order.product_type === 'vinyl' || order.media_type === 'vinyl') ? (
                                        <div className="flex justify-between">
                                            <span className="text-slate-400">Format</span>
                                            <span className="text-slate-700 font-medium" data-testid="order-specs-format">
                                                {vinylSpecs?.format || order.format || '—'}
                                            </span>
                                        </div>
                                    ) : Array.isArray(order.master_slots) && order.master_slots.length > 0 ? (
                                        <div className="flex justify-between">
                                            <span className="text-slate-400">Discs</span>
                                            <span className="text-slate-700 font-medium" data-testid="order-specs-discs">
                                                {order.master_slots.length}
                                            </span>
                                        </div>
                                    ) : null}
                                    <div className="flex justify-between items-center">
                                        <span className="text-slate-400">Quantity</span>
                                        <span className="text-slate-700 font-medium flex items-center gap-1.5" data-testid="order-specs-quantity">
                                            {order.quantity || vinylSpecs?.quantity || '—'}
                                            {isAdmin && order.status !== 'draft' && (
                                                <button
                                                    type="button"
                                                    onClick={openQuantityDialog}
                                                    className="p-0.5 rounded hover:bg-slate-100 text-slate-400 hover:text-blue-600 transition-colors"
                                                    title="Admin: Update quantity (manual override)"
                                                    data-testid="admin-edit-quantity-btn"
                                                >
                                                    <Pencil className="h-3 w-3" />
                                                </button>
                                            )}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-400">Delivery</span>
                                        <span className="text-slate-700 font-medium" data-testid="admin-delivery">
                                            {order.delivery_date
                                                ? formatDate(order.delivery_date)
                                                : order.dispatch_note_date
                                                    ? formatDate(order.dispatch_note_date)
                                                    : order.requested_delivery_date
                                                        ? formatDate(order.requested_delivery_date)
                                                        : '—'}
                                        </span>
                                    </div>
                                    {/* Dispatch note date — surfaces the planned arrival pulled
                                        from a linked inbound dispatch document. Shown as its own
                                        row when there's also a manual delivery_date so the admin
                                        can see both values without going into the supplier section. */}
                                    {order.dispatch_note_date && order.delivery_date && order.dispatch_note_date !== order.delivery_date && (
                                        <div className="flex justify-between">
                                            <span className="text-slate-400">Dispatch note</span>
                                            <span className="text-slate-600 font-medium italic" data-testid="admin-dispatch-note-date">
                                                {formatDate(order.dispatch_note_date)}
                                            </span>
                                        </div>
                                    )}
                                    {/* Customer-requested delivery date — surfaced separately
                                        when both fields are set so admin knows what the
                                        customer asked vs what's been promised. */}
                                    {order.delivery_date && order.requested_delivery_date && (
                                        <div className="flex justify-between">
                                            <span className="text-slate-400">Requested</span>
                                            <span className="text-slate-600 font-medium" data-testid="admin-requested-delivery">
                                                {formatDate(order.requested_delivery_date)}
                                            </span>
                                        </div>
                                    )}
                                    {order.customer_po_number && (
                                        <div className="flex justify-between">
                                            <span className="text-slate-400">PO #</span>
                                            <span className="text-slate-700 font-medium font-mono text-[11px]" data-testid="admin-customer-po">
                                                {order.customer_po_number}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            {/* Timestamps - Compact */}
                            <div className="bg-white rounded-lg p-3 border border-slate-300 shadow-premium-sm">
                                <div className="flex items-center justify-between text-xs mb-1">
                                    <span className="text-slate-400">Created</span>
                                    <span className="text-slate-600">{formatDate(order.created_at)}</span>
                                </div>
                                <div className="flex items-center justify-between text-xs">
                                    <span className="text-slate-400">Updated</span>
                                    <span className="text-slate-600">{formatDate(order.updated_at)}</span>
                                </div>
                            </div>

                            {/* Test Press — manual workflow flag. Both admins
                                and the order owner can update it (`_check_order_access`
                                gates non-owners on the backend). Hidden entirely
                                for order types where it doesn't apply — currently
                                shown for vinyl AND for any non-vinyl order that
                                already carries a non-null test_press_status
                                (so an admin who accidentally set it on a CD can
                                still see + reset it). */}
                            {/* Test Press card used to live here. Moved to
                                the Working Area between Artwork and NCB
                                Clearance so admins don't have duplicate
                                workflow controls on the same page.
                                Customer-side TP still renders at the top
                                of OrderFilesPage. */}
                            
                            {/* Production Details - shows option attribute selections */}
                            {order?.specs?._option_attributes && Object.keys(order.specs._option_attributes).length > 0 && (
                                <ProductionDetailsSection 
                                    optionAttributes={order.specs._option_attributes}
                                    className="border-slate-300 shadow-premium-sm"
                                />
                            )}

                            {/* Customer Note — the `notes` field is populated by the
                                simple CD/DVD/Bluray order form (SimpleMediaOrderPage).
                                Surfaced here so admins notice instructions like
                                "Den skal være med det nye cover" without having to
                                dig through the raw order document. `whitespace-pre-wrap`
                                preserves the line breaks the customer typed. */}
                            {order?.notes && order.notes.trim() && (
                                <div
                                    className="bg-amber-50 rounded-lg p-3 border border-amber-200 shadow-premium-sm"
                                    data-testid="admin-customer-note"
                                >
                                    <div className="text-[10px] uppercase text-amber-700 mb-2 flex items-center gap-1.5 font-semibold tracking-wide">
                                        <MessageSquare className="h-3 w-3" />
                                        Customer Note
                                    </div>
                                    <p className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">
                                        {order.notes}
                                    </p>
                                    <p className="text-[10px] text-slate-400 mt-2">
                                        Submitted by customer with this order
                                    </p>
                                </div>
                            )}

                            {/* Profit & Margin Summary */}
                            <div className="bg-white rounded-lg p-3 border border-slate-300 shadow-premium-sm">
                                <div className="text-[10px] uppercase text-slate-400 mb-2 flex items-center justify-between">
                                    <span>Finance Summary</span>
                                    {financeSummary.isEstimate && financeSummary.profit !== null && (
                                        <span className="text-amber-600 normal-case">Est.</span>
                                    )}
                                </div>
                                {financeSummary.profit !== null ? (
                                    <div className="space-y-2">
                                        {/* Sales Revenue */}
                                        <div className="flex justify-between items-baseline">
                                            <span className="text-xs text-slate-400">Revenue</span>
                                            <div className="text-right">
                                                {financeSummary.hasDkkValues ? (
                                                    <>
                                                        <span className="text-sm font-semibold text-slate-700">
                                                            {financeSummary.revenueOriginal?.toLocaleString('da-DK', { minimumFractionDigits: 2 })} {financeSummary.originalCurrency}
                                                        </span>
                                                        <span className="text-[10px] text-slate-400 ml-1">
                                                            ({financeSummary.revenueEur?.toLocaleString('da-DK', { minimumFractionDigits: 2 })} EUR)
                                                        </span>
                                                    </>
                                                ) : (
                                                    <span className="text-sm font-semibold text-slate-700">
                                                        {financeSummary.revenueEur?.toLocaleString('da-DK', { minimumFractionDigits: 2 })} EUR
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        
                                        {/* Profit */}
                                        <div className="flex justify-between items-baseline">
                                            <span className="text-xs text-slate-400">Profit</span>
                                            <div className="text-right">
                                                {financeSummary.hasDkkValues ? (
                                                    <>
                                                        <span className={`text-sm font-bold ${financeSummary.profitOriginal >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-testid="profit-amount">
                                                            {financeSummary.profitOriginal >= 0 ? '+' : ''}{financeSummary.profitOriginal?.toLocaleString('da-DK', { minimumFractionDigits: 2 })} {financeSummary.originalCurrency}
                                                        </span>
                                                        <span className="text-[10px] text-slate-400 ml-1">
                                                            ({financeSummary.profitEur >= 0 ? '+' : ''}{financeSummary.profitEur?.toLocaleString('da-DK', { minimumFractionDigits: 2 })} EUR)
                                                        </span>
                                                    </>
                                                ) : (
                                                    <span className={`text-sm font-bold ${financeSummary.profitEur >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-testid="profit-amount">
                                                        {financeSummary.profitEur >= 0 ? '+' : ''}{financeSummary.profitEur?.toLocaleString('da-DK', { minimumFractionDigits: 2 })} EUR
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        
                                        {/* Margin */}
                                        <div className="flex justify-between items-baseline pt-1 border-t border-slate-100">
                                            <span className="text-xs text-slate-400">Margin</span>
                                            <span className={`text-sm font-bold ${
                                                financeSummary.margin >= 20 ? 'text-emerald-600' : 
                                                financeSummary.margin >= 0 ? 'text-amber-600' : 'text-red-600'
                                            }`}>
                                                {financeSummary.margin?.toFixed(1)}%
                                            </span>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="text-lg font-bold text-slate-300">—</div>
                                        <div className="text-xs text-slate-400">Set prices in Finance</div>
                                    </>
                                )}
                            </div>

                            {/* Admin Notes — internal free-text notepad on
                                the order. Renders nothing for non-admin
                                users. Autosaves as the admin types. Kept
                                INSIDE the sticky container so it scrolls
                                together with the rest of the sidebar
                                instead of sliding underneath Finance
                                Summary. */}
                            {isAdmin && (
                                <AdminNotesCard
                                    order={order}
                                    onUpdated={(updated) => setOrder((prev) => ({ ...prev, ...updated }))}
                                />
                            )}
                        </div>
                    </div>

                    {/* MAIN WORK AREA */}
                    <div className="col-span-12 lg:col-span-7">
                        <CollapsibleSectionsProvider>
                            <div className="flex items-center justify-between mb-3">
                                <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 px-1">Working Area</div>
                                {isAdmin && <CollapsibleSectionsControls />}
                            </div>
                        
                            {/* 1. Master - First section */}
                            {(() => {
                                // Surface unresolved checksum problems at the
                                // section-header level so admins can see "MD5
                                // Issue" instead of a misleading "Sony: Received"
                                // badge when a master file is missing its hash
                                // or fails verification. We treat both states
                                // as blockers because Sony's "received" event
                                // only confirms the bytes landed in their
                                // ingest pipeline — it does NOT mean our
                                // checksum manifest matched.
                                const md5IssueFiles = (audioFiles || []).filter((f) =>
                                    !f.superseded
                                    && !f.is_checksum_file
                                    && (!f.md5_checksum || f.md5_verified === 'mismatch')
                                );
                                const hasMd5Issue = md5IssueFiles.length > 0;
                                // Supplier (Sony) status is INTERNAL — only surfaced
                                // to admins. Customers see the primary Master status
                                // only.
                                const supplierLabel = isAdmin ? (
                                    order?.audio_supplier_status === 'received' ? 'Sony: Received' :
                                    order?.audio_supplier_status === 'uploaded' ? 'Sony: Uploaded' :
                                    order?.audio_supplier_status === 'error' ? 'Sony: Error' :
                                    audioFiles.length > 0 ? 'Sony: Awaiting' : null
                                ) : null;
                                return (
                            <AdminCollapsibleSection
                                id="audio"
                                title="Master"
                                icon={Music}
                                iconColor={hasMd5Issue ? 'text-rose-500' : (audioFiles.length > 0 ? 'text-emerald-500' : 'text-blue-500')}
                                status={hasMd5Issue ? 'attention' : (audioFiles.length > 0 ? 'uploaded' : 'not_started')}
                                statusLabel={
                                    hasMd5Issue
                                        ? `MD5 issue · ${md5IssueFiles.length} file${md5IssueFiles.length > 1 ? 's' : ''}`
                                        : (audioFiles.length > 0 ? `${audioFiles.length} file${audioFiles.length > 1 ? 's' : ''}` : 'Not uploaded')
                                }
                                secondaryStatus={isAdmin ? (hasMd5Issue ? 'error' : (order?.audio_supplier_status === 'uploaded' ? 'supplier_uploaded' : (order?.audio_supplier_status || (audioFiles.length > 0 ? 'awaiting' : null)))) : null}
                                secondaryStatusLabel={isAdmin ? (hasMd5Issue ? 'Checksum unresolved' : supplierLabel) : null}
                                defaultOpen={hasMd5Issue}
                                summary={
                                    hasMd5Issue
                                        ? 'One or more master files have unresolved checksum problems — review below.'
                                        : order?.audio_master_reupload_required
                                            ? 'A new master is required for the Test Press — awaiting customer upload'
                                            : (audioFiles.length > 0 ? 'Master files ready for production' : 'Upload master files')
                                }
                                attentionRequired={hasMd5Issue || (isAdmin && order?.audio_supplier_status === 'error') || !!order?.audio_master_reupload_required}
                                nextActionOwner={
                                    (hasMd5Issue || (isAdmin && order?.audio_supplier_status === 'error'))
                                        ? 'admin'
                                        : (order?.audio_master_reupload_required || audioFiles.length === 0)
                                            ? 'customer'
                                            : null
                                }
                            >
                                {/* Customer Upload Status */}
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs font-medium text-slate-600">Customer Upload:</span>
                                            <span className={`text-xs font-medium ${audioFiles.length > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                                                {audioFiles.length > 0 ? `${audioFiles.length} file${audioFiles.length > 1 ? 's' : ''} uploaded` : 'Not uploaded'}
                                            </span>
                                        </div>
                                        {/* Admin-only: re-run MD5 verification for ALL masters +
                                           sidecars in this order. Repairs legacy orders where
                                           masters are present + Sony marked received but the
                                           Master status still shows Missing because verification
                                           was never wired. */}
                                        {/* Two-workflow admin actions:
                                            (1) Generate missing MD5 — only enabled
                                                when at least one master file has no
                                                stored hash. Streams every such file
                                                from S3, computes the hash, then
                                                verifies. Expensive on big DDP sets so
                                                we gate it behind a confirm dialog.
                                            (2) Verify MD5 — pure re-matching against
                                                sidecars. Cheap. Always available.   */}
                                        {audioFiles.length > 0 && (() => {
                                            const filesMissingMd5 = audioFiles.filter(
                                                f => !f.is_checksum_file
                                                    && !f.superseded
                                                    && !f.md5_checksum
                                            );
                                            return (
                                                <>
                                                    {filesMissingMd5.length > 0 && (
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="h-7 text-[11px] gap-1.5 border-rose-300 text-rose-700 hover:bg-rose-50"
                                                            onClick={async () => {
                                                                if (isGeneratingMissingMd5) return;
                                                                if (!window.confirm(
                                                                    `Generate MD5 for ${filesMissingMd5.length} file(s)? This streams each file from S3 and may take several minutes for large masters.`
                                                                )) return;
                                                                setIsGeneratingMissingMd5(true);
                                                                try {
                                                                    const res = await filesApi.generateMissingMd5(orderId);
                                                                    const s = res.data || {};
                                                                    const parts = [];
                                                                    if (s.files_regenerated) parts.push(`${s.files_regenerated} hash(es) generated`);
                                                                    if (s.files_skipped) parts.push(`${s.files_skipped} skipped`);
                                                                    if ((s.errors || []).length) parts.push(`${s.errors.length} error(s)`);
                                                                    toast.success(parts.length ? parts.join(' · ') : 'No files needed MD5 generation', { duration: 8000 });
                                                                    const fresh = await ordersApi.getFiles(orderId);
                                                                    setFiles(fresh.data || []);
                                                                } catch (err) {
                                                                    toast.error(err.response?.data?.detail || 'MD5 generation failed');
                                                                } finally {
                                                                    setIsGeneratingMissingMd5(false);
                                                                }
                                                            }}
                                                            disabled={isGeneratingMissingMd5}
                                                            data-testid="generate-missing-md5-btn"
                                                            title={`Stream-download ${filesMissingMd5.length} file(s) from S3 and compute MD5 hashes`}
                                                        >
                                                            {isGeneratingMissingMd5
                                                                ? <Loader2 className="h-3 w-3 animate-spin" />
                                                                : <RefreshCw className="h-3 w-3" />}
                                                            Generate missing MD5 ({filesMissingMd5.length})
                                                        </Button>
                                                    )}
                                                    {/* ── Generate missing MD5 FILES ──
                                                        Writes a ``.md5`` sidecar next to each
                                                        master file that already has a trusted
                                                        server-computed hash but no customer
                                                        ``.md5`` covering it. Idempotent;
                                                        historical Sony jobs are never touched
                                                        (only newly built Sony plans pick up
                                                        the generated sidecars). */}
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="h-7 text-[11px] gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                                        onClick={async () => {
                                                            if (isGeneratingMd5Sidecars) return;
                                                            setIsGeneratingMd5Sidecars(true);
                                                            try {
                                                                const preview = await filesApi.previewMd5SidecarPlan(orderId);
                                                                setMd5SidecarPlan(preview.data);
                                                            } catch (err) {
                                                                toast.error(err.response?.data?.detail || 'Could not build sidecar plan');
                                                                setIsGeneratingMd5Sidecars(false);
                                                            }
                                                        }}
                                                        disabled={isGeneratingMd5Sidecars}
                                                        data-testid="generate-md5-sidecars-btn"
                                                        title="Generate one .md5 sidecar per master file that has no customer-provided MD5."
                                                    >
                                                        {isGeneratingMd5Sidecars && !md5SidecarPlan
                                                            ? <Loader2 className="h-3 w-3 animate-spin" />
                                                            : <FileText className="h-3 w-3" />}
                                                        Generate missing MD5 files
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="h-7 text-[11px] gap-1.5"
                                                        onClick={async () => {
                                                            if (isReVerifying) return;
                                                            setIsReVerifying(true);
                                                            try {
                                                                const res = await filesApi.reVerifyMasterFiles(orderId);
                                                                const s = res.data || {};
                                                                const parts = [];
                                                                if (s.masters_now_match) parts.push(`${s.masters_now_match} master(s) verified`);
                                                                if (s.masters_now_mismatch) parts.push(`${s.masters_now_mismatch} mismatch`);
                                                                if (s.sidecars_processed) parts.push(`${s.sidecars_processed} sidecar(s) reparsed`);
                                                                if ((s.masters_without_md5 || []).length) {
                                                                    parts.push(`${s.masters_without_md5.length} file(s) need MD5 generation`);
                                                                }
                                                                toast.success(parts.length ? parts.join(' · ') : 'Verification complete', { duration: 8000 });
                                                                const fresh = await ordersApi.getFiles(orderId);
                                                                setFiles(fresh.data || []);
                                                            } catch (err) {
                                                                toast.error(err.response?.data?.detail || 'Verification failed');
                                                            } finally {
                                                                setIsReVerifying(false);
                                                            }
                                                        }}
                                                        disabled={isReVerifying}
                                                        data-testid="verify-md5-btn"
                                                        title="Re-run sidecar matching for every master with a stored hash. Never modifies hashes."
                                                    >
                                                        {isReVerifying
                                                            ? <Loader2 className="h-3 w-3 animate-spin" />
                                                            : <ShieldCheck className="h-3 w-3" />}
                                                        Verify MD5
                                                    </Button>
                                                </>
                                            );
                                        })()}
                                    </div>
                                    
                                    {/* Audio Files List. When the order has
                                        more than one master slot OR any file
                                        is tagged with a `_layerN` category
                                        (DVD-9 dual-layer), we switch to a
                                        disc-grouped view so the layer split
                                        is visually obvious. Everything else
                                        (single-disc, single-layer) keeps the
                                        flat layout. */}
                                    {audioFiles.length > 0 && (
                                        (
                                            (order?.master_slots?.length || 0) > 1
                                            || audioFiles.some(f => /_layer[01]$/.test(f.category || ''))
                                        ) ? (
                                            <AdminMasterDiscGroups
                                                order={order}
                                                audioFiles={audioFiles}
                                                onDownloadFile={handleDownloadFile}
                                                onOrderUpdated={setOrder}
                                                onFileUpdated={(updatedFile) => {
                                                    setFiles((prev) =>
                                                        prev.map((f) => (f.id === updatedFile.id ? { ...f, ...updatedFile } : f)),
                                                    );
                                                }}
                                            />
                                        ) : (
                                            <div className="space-y-1">
                                                {audioFiles.map(file => (
                                                    <AdminMasterFileRow
                                                        key={file.id}
                                                        file={file}
                                                        subLabel={getMasterFileSubLabel(file, order)}
                                                        onDownload={handleDownloadFile}
                                                        onReject={isAdmin ? setRejectingMasterFile : undefined}
                                                        onFileUpdated={(updatedFile) => {
                                                            setFiles((prev) =>
                                                                prev.map((f) => (f.id === updatedFile.id ? { ...f, ...updatedFile } : f)),
                                                            );
                                                        }}
                                                    />
                                                ))}
                                            </div>
                                        )
                                    )}
                                    
                                    {/* Tracklist Files */}
                                    {tracklistFiles.length > 0 && (
                                        <div className="space-y-1 pt-2 border-t">
                                            <span className="text-xs text-slate-500">Tracklist:</span>
                                            {tracklistFiles.map(file => (
                                                <div key={file.id} className="flex items-center justify-between py-1.5 px-2 bg-background rounded text-xs">
                                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                                        <FileText className="h-3.5 w-3.5 text-slate-500 flex-shrink-0" />
                                                        <span className="truncate">{file.original_filename}</span>
                                                    </div>
                                                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 flex-shrink-0" onClick={() => handleDownloadFile(file)}>
                                                        <Download className="h-3 w-3" />
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    
                                    {/* Supplier Confirmation Status - Admin Only */}
                                    {isAdmin && audioFiles.length > 0 && (
                                        <div className="pt-3 border-t">
                                            <div className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs font-medium text-slate-600">Supplier Status:</span>
                                                    <span className={`text-xs font-medium ${
                                                        order?.audio_supplier_status === 'received' ? 'text-emerald-600' :
                                                        order?.audio_supplier_status === 'uploaded' ? 'text-indigo-600' :
                                                        order?.audio_supplier_status === 'error' ? 'text-red-600' :
                                                        order?.audio_supplier_status === 'awaiting' ? 'text-amber-600' :
                                                        'text-slate-400'
                                                    }`}>
                                                        {order?.audio_supplier_status === 'received' ? '✓ Received by Sony' :
                                                         order?.audio_supplier_status === 'uploaded' ? '↑ Uploaded to Sony' :
                                                         order?.audio_supplier_status === 'error' ? '✗ Ingestion Error' :
                                                         order?.audio_supplier_status === 'awaiting' ? '⏳ Awaiting confirmation' :
                                                         'Not sent yet'}
                                                    </span>
                                                </div>
                                                <Select 
                                                    value={order?.audio_supplier_status || ''} 
                                                    onValueChange={async (value) => {
                                                        try {
                                                            await ordersApi.update(order.id, {});
                                                            const res = await fetch(`${API_BASE}/api/orders/${order.id}/audio-supplier-status`, {
                                                                method: 'PATCH',
                                                                headers: {
                                                                    'Content-Type': 'application/json',
                                                                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                                                                },
                                                                body: JSON.stringify({ status: value })
                                                            });
                                                            if (res.ok) {
                                                                toast.success('Supplier status updated');
                                                                fetchData();
                                                            }
                                                        } catch (err) {
                                                            toast.error('Failed to update status');
                                                        }
                                                    }}
                                                >
                                                    <SelectTrigger className="w-32 h-7 text-xs" data-testid="audio-supplier-status-select">
                                                        <SelectValue placeholder="Set status" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="awaiting">Awaiting</SelectItem>
                                                        <SelectItem value="uploaded">Uploaded</SelectItem>
                                                        <SelectItem value="received">Received</SelectItem>
                                                        <SelectItem value="error">Error</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            {order?.audio_supplier_note && (
                                                <p className="text-[10px] text-slate-400 mt-1 px-3">
                                                    Note: {order.audio_supplier_note}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                    
                                    {audioFiles.length === 0 && (
                                        <div className="text-center py-4 text-xs text-slate-400">No audio files uploaded</div>
                                    )}
                                    {/* Sony master upload (Phase 1) — admin only.
                                        Builds/validates the Sony job JSON the future
                                        EC2 worker will consume. Placed inside the
                                        Master section because it uploads master files,
                                        not PO metadata. */}
                                    {isAdmin && <SonyUploadPanel orderId={order.id} />}
                                </div>
                            </AdminCollapsibleSection>
                                );
                            })()}

                            {/* 2. Artwork - The central artwork workflow job */}
                            {isAdmin && (
                                <AdminCollapsibleSection
                                    id="artwork"
                                    title="Artwork"
                                    icon={Palette}
                                    iconColor={artworkJob ? 'text-purple-500' : 'text-slate-400'}
                                    status={artworkJob ? (
                                        artworkJob.artwork_status === 'rejected' ? 'attention' :
                                        artworkJob.artwork_status === 'approved' ? 'approved' :
                                        ['Approved', 'Completed'].includes(artworkJob.status) ? 'approved' :
                                        artworkJob.status === 'Awaiting_Approval' ? 'attention' : 'in_progress'
                                    ) : 'not_started'}
                                    statusLabel={artworkJob ? (
                                        artworkJob.artwork_status === 'rejected' ? 'Revision Needed' :
                                        artworkJob.artwork_status === 'approved' ? 'Approved' :
                                        artworkJob.status.replace(/_/g, ' ')
                                    ) : 'Missing'}
                                    secondaryStatus={order?.artwork_supplier_status || (artworkJob && ['Approved', 'Completed'].includes(artworkJob.status) ? 'awaiting' : null)}
                                    secondaryStatusLabel={
                                        order?.artwork_supplier_status === 'received' ? 'Sony: Received' :
                                        order?.artwork_supplier_status === 'error' ? 'Sony: Error' :
                                        (artworkJob && ['Approved', 'Completed'].includes(artworkJob.status)) ? 'Sony: Awaiting' : null
                                    }
                                    attentionRequired={artworkJob?.artwork_status === 'rejected' || artworkJob?.status === 'Awaiting_Approval' || order?.artwork_supplier_status === 'error'}
                                    nextActionOwner={
                                        order?.artwork_supplier_status === 'error'
                                            ? 'admin'
                                            : (artworkJob?.status === 'Awaiting_Approval'
                                                || artworkJob?.artwork_status === 'rejected')
                                                ? 'customer'
                                                : null
                                    }
                                    defaultOpen={artworkJob?.artwork_status === 'rejected'}
                                    summary="Artwork correction and approval workflow"
                                >
                                    {artworkJob ? (
                                        <div className="space-y-3 pt-2">
                                            <div className="flex items-center justify-between">
                                                <div className="space-y-1">
                                                    <div className="flex items-center gap-2 text-sm">
                                                        <Building2 className="h-4 w-4 text-slate-400" />
                                                        <span className="font-medium">{artworkJob.partner_name || 'Unassigned'}</span>
                                                        {artworkJob.assigned_to_user_name && (
                                                            <span className="text-slate-400">({artworkJob.assigned_to_user_name})</span>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-4 text-xs text-slate-500">
                                                        <span>Files: {artworkJob.artwork_files?.length || 0} original, {artworkJob.repro_files?.length || 0} corrected</span>
                                                    </div>
                                                </div>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => navigate(`/artwork-jobs/${artworkJob.id}`)}
                                                    data-testid="view-artwork-job-btn"
                                                >
                                                    <ExternalLink className="h-4 w-4 mr-1" />
                                                    View Job
                                                </Button>
                                            </div>
                                            {artworkJob.instructions && (
                                                <div className="text-xs text-slate-500 bg-background p-2 rounded border">
                                                    <strong>Instructions:</strong> {artworkJob.instructions}
                                                </div>
                                            )}
                                            {/* Delivery note from repro partner — surfaced after Mark as Delivered to Factory */}
                                            {artworkJob.factory_delivery_note && (
                                                <div className="rounded border border-emerald-200 bg-emerald-50/60 p-3" data-testid="artwork-delivery-note">
                                                    <div className="flex items-center justify-between mb-1">
                                                        <span className="text-xs font-semibold uppercase tracking-wide text-emerald-800">
                                                            Message from repro partner
                                                        </span>
                                                        {artworkJob.delivered_to_factory_at && (
                                                            <span className="text-[11px] text-emerald-700">
                                                                {artworkJob.delivered_to_factory_by_name || '—'} • {formatDate(artworkJob.delivered_to_factory_at)}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-sm text-slate-700 whitespace-pre-wrap">
                                                        {artworkJob.factory_delivery_note}
                                                    </p>
                                                </div>
                                            )}

                                            {/* Manual Supplier Status control — mirrors the
                                                identical control on the Master/Audio section.
                                                Admin uses it to track whether the supplier
                                                (Sony / pressing plant) has confirmed receipt of
                                                the artwork files. Independent from `artwork_status`
                                                which is the customer-side approval. */}
                                            <div className="pt-3 border-t">
                                                <div className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xs font-medium text-slate-600">Supplier Status:</span>
                                                        <span className={`text-xs font-medium ${
                                                            order?.artwork_supplier_status === 'received' ? 'text-emerald-600' :
                                                            order?.artwork_supplier_status === 'error' ? 'text-red-600' :
                                                            order?.artwork_supplier_status === 'awaiting' ? 'text-amber-600' :
                                                            'text-slate-400'
                                                        }`}>
                                                            {order?.artwork_supplier_status === 'received' ? '✓ Received by Sony' :
                                                             order?.artwork_supplier_status === 'error' ? '✗ Ingestion Error' :
                                                             order?.artwork_supplier_status === 'awaiting' ? '⏳ Awaiting confirmation' :
                                                             'Not sent yet'}
                                                        </span>
                                                    </div>
                                                    <Select
                                                        value={order?.artwork_supplier_status || ''}
                                                        onValueChange={async (value) => {
                                                            try {
                                                                const res = await fetch(`${API_BASE}/api/orders/${order.id}/artwork-supplier-status`, {
                                                                    method: 'PATCH',
                                                                    headers: {
                                                                        'Content-Type': 'application/json',
                                                                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                                                                    },
                                                                    body: JSON.stringify({ status: value })
                                                                });
                                                                if (res.ok) {
                                                                    toast.success('Artwork supplier status updated');
                                                                    fetchData();
                                                                } else {
                                                                    toast.error('Failed to update status');
                                                                }
                                                            } catch (err) {
                                                                toast.error('Failed to update status');
                                                            }
                                                        }}
                                                    >
                                                        <SelectTrigger className="w-32 h-7 text-xs" data-testid="artwork-supplier-status-select">
                                                            <SelectValue placeholder="Set status" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="awaiting">Awaiting</SelectItem>
                                                            <SelectItem value="received">Received</SelectItem>
                                                            <SelectItem value="error">Error</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                {order?.artwork_supplier_note && (
                                                    <p className="text-[10px] text-slate-400 mt-1 px-3">
                                                        Note: {order.artwork_supplier_note}
                                                    </p>
                                                )}

                                                {/* Sony Graphic Upload validation card.
                                                    Populated by the supplier_artwork_received
                                                    inbound event: compares Sony's PDF count
                                                    with the .pdf files in this order's repro
                                                    job. Mismatches are surfaced so the admin
                                                    can resend missing files. */}
                                                {order?.artwork_supplier_validation && (
                                                    <div
                                                        className={`mt-2 rounded-lg border p-3 text-xs ${
                                                            order.artwork_supplier_validation.validation_status === 'ok'
                                                                ? 'border-emerald-200 bg-emerald-50'
                                                                : 'border-amber-200 bg-amber-50'
                                                        }`}
                                                        data-testid="artwork-supplier-validation-card"
                                                    >
                                                        <div className="flex items-center justify-between mb-2">
                                                            <span className="font-semibold text-slate-700">
                                                                Sony Graphic Upload — Validation
                                                            </span>
                                                            <span
                                                                className={`text-[11px] font-semibold px-2 py-0.5 rounded ${
                                                                    order.artwork_supplier_validation.validation_status === 'ok'
                                                                        ? 'bg-emerald-100 text-emerald-700'
                                                                        : 'bg-amber-100 text-amber-700'
                                                                }`}
                                                                data-testid="artwork-validation-status"
                                                            >
                                                                {order.artwork_supplier_validation.validation_status === 'ok' ? 'OK' : 'Mismatch'}
                                                            </span>
                                                        </div>
                                                        <div className="grid grid-cols-3 gap-2 mb-2">
                                                            <div>
                                                                <div className="text-[10px] uppercase tracking-wide text-slate-500">FLOW approved PDFs</div>
                                                                <div className="text-base font-semibold text-slate-800" data-testid="flow-pdf-count">
                                                                    {order.artwork_supplier_validation.flow_pdf_count ?? 0}
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <div className="text-[10px] uppercase tracking-wide text-slate-500">Sony received PDFs</div>
                                                                <div className="text-base font-semibold text-slate-800" data-testid="sony-pdf-count">
                                                                    {order.artwork_supplier_validation.sony_received_pdf_count ?? 0}
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <div className="text-[10px] uppercase tracking-wide text-slate-500">Ignored files</div>
                                                                <div className="text-base font-semibold text-slate-800" data-testid="ignored-count">
                                                                    {order.artwork_supplier_validation.ignored_count ?? 0}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {order.artwork_supplier_validation.warning_message && (
                                                            <p className="text-[11px] text-amber-700 font-medium mb-1" data-testid="artwork-validation-warning">
                                                                ⚠ {order.artwork_supplier_validation.warning_message}
                                                            </p>
                                                        )}
                                                        {(order.artwork_supplier_validation.ignored_files || []).length > 0 && (
                                                            <details className="mt-1">
                                                                <summary className="cursor-pointer text-[10px] text-slate-500 hover:text-slate-700">
                                                                    Ignored file names ({order.artwork_supplier_validation.ignored_files.length})
                                                                </summary>
                                                                <ul className="ml-3 mt-1 list-disc text-[10px] text-slate-600">
                                                                    {order.artwork_supplier_validation.ignored_files.map((f, i) => (
                                                                        <li key={i}>{typeof f === 'string' ? f : (f?.filename || JSON.stringify(f))}</li>
                                                                    ))}
                                                                </ul>
                                                            </details>
                                                        )}
                                                        {(order.artwork_supplier_validation.sony_pdf_files || []).length > 0 && (
                                                            <details className="mt-1">
                                                                <summary className="cursor-pointer text-[10px] text-slate-500 hover:text-slate-700">
                                                                    Sony received PDFs ({order.artwork_supplier_validation.sony_pdf_files.length})
                                                                </summary>
                                                                <ul className="ml-3 mt-1 list-disc text-[10px] text-slate-600">
                                                                    {order.artwork_supplier_validation.sony_pdf_files.map((f, i) => (
                                                                        <li key={i}>{typeof f === 'string' ? f : (f?.filename || JSON.stringify(f))}</li>
                                                                    ))}
                                                                </ul>
                                                            </details>
                                                        )}
                                                        {order.artwork_supplier_validation.checked_at && (
                                                            <p className="text-[10px] text-slate-400 mt-1">
                                                                Checked {new Date(order.artwork_supplier_validation.checked_at).toLocaleString()}
                                                            </p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex items-center justify-between pt-2">
                                            <p className="text-xs text-muted-foreground">No artwork job created for this order yet</p>
                                            <Button
                                                size="sm"
                                                onClick={() => setIsCreateArtworkJobDialogOpen(true)}
                                                className="bg-purple-600 hover:bg-purple-700"
                                                data-testid="create-artwork-job-btn"
                                            >
                                                <Palette className="h-4 w-4 mr-1" />
                                                Create Artwork Job
                                            </Button>
                                        </div>
                                    )}
                                </AdminCollapsibleSection>
                            )}

                            {/* Test Press — Working Area section (moved from
                                sidebar). Only renders when a TP is required/
                                active. Collapsed row surfaces the current
                                status + "Action needed" when the admin
                                still has to do something; expanded view
                                hosts the existing TestPressCard with its
                                approve/reject + comment UI. Reuses the
                                existing state machine; no parallel logic. */}
                            {isAdmin
                                && order?.test_press_status
                                && order.test_press_status !== 'not_required' && (() => {
                                    const tp = order.test_press_status;
                                    // Admin action-needed states: TP has
                                    // arrived at the customer (chase the
                                    // decision) OR customer rejected (admin
                                    // must send feedback to Sony — Phase 3).
                                    const actionNeeded = tp === 'arrived' || tp === 'rejected';
                                    const statusLabel = ({
                                        required:   'Ordered',
                                        ordered:    'In production',
                                        confirmed:  'Confirmed',
                                        dispatched: 'Dispatched',
                                        arrived:    'Delivered',
                                        approved:   'Approved',
                                        rejected:   'Rejected',
                                        awaiting_feedback: 'Awaiting Sony',
                                    })[tp] || tp;
                                    const iconColor = ({
                                        approved: 'text-emerald-500',
                                        rejected: 'text-red-500',
                                        arrived:  'text-amber-500',
                                        awaiting_feedback: 'text-sky-500',
                                        required: 'text-blue-500',
                                        ordered:  'text-blue-500',
                                    })[tp] || 'text-indigo-500';
                                    return (
                                        <AdminCollapsibleSection
                                            id="test-press"
                                            title="Test Press"
                                            icon={Disc3}
                                            iconColor={iconColor}
                                            status={tp}
                                            statusLabel={statusLabel}
                                            attentionRequired={actionNeeded}
                                            nextActionOwner={
                                                tp === 'arrived' ? 'customer' :
                                                tp === 'rejected' ? 'admin' :
                                                tp === 'awaiting_feedback' ? 'supplier' :
                                                null
                                            }
                                            defaultOpen={actionNeeded}
                                            summary={
                                                tp === 'arrived'
                                                    ? 'Delivered to customer — awaiting their decision'
                                                    : tp === 'rejected'
                                                        ? 'Rejected by customer — send feedback to Sony'
                                                        : tp === 'awaiting_feedback'
                                                            ? 'Feedback sent to Sony — awaiting their response'
                                                            : tp === 'approved'
                                                                ? 'Approved by customer — ready for final BOM'
                                                                : (tp === 'required' || tp === 'ordered')
                                                                    ? (tp === 'ordered'
                                                                        ? 'Test Press in production at supplier — awaiting confirmation'
                                                                        : 'Test Press is included — pending supplier confirmation')
                                                                    : 'Test press workflow in progress'
                                            }
                                        >
                                            <TestPressCard
                                                order={order}
                                                onUpdated={(updated) =>
                                                    setOrder((prev) => ({ ...prev, ...(updated || {}) }))
                                                }
                                            />
                                        </AdminCollapsibleSection>
                                    );
                                })()}

                            {/* 2. NCB Clearance - Admin only, always visible */}
                            {isAdmin && (
                                <AdminCollapsibleSection
                                    id="ncb"
                                    title="NCB Clearance"
                                    icon={Shield}
                                    iconColor={order?.ncb_clearance?.status === 'cleared' ? 'text-emerald-500' : 'text-amber-500'}
                                    status={order?.ncb_clearance?.status || 'pending'}
                                    statusLabel={order?.ncb_clearance?.status === 'cleared' ? 'Cleared' : 
                                               order?.ncb_clearance?.status === 'follow_up_required' ? 'Follow-up Required' : 'Pending'}
                                    attentionRequired={order?.ncb_clearance?.status === 'follow_up_required'}
                                    nextActionOwner={
                                        (order?.ncb_clearance?.status === 'follow_up_required'
                                         || order?.ncb_clearance?.status === 'pending')
                                            ? 'customer'
                                            : null
                                    }
                                    defaultOpen={false}
                                    summary="Rights clearance status for production"
                                >
                                    <NCBClearanceSection 
                                        order={order} 
                                        onUpdate={fetchData}
                                        embedded={true}
                                    />
                                </AdminCollapsibleSection>
                            )}

                            {/* 4. Supplier Workflow */}
                            {isAdmin && (
                                <AdminCollapsibleSection
                                    id="supplier"
                                    title={order?.supplier_mode === 'none' ? 'Supplier (Disabled)' : 'Supplier (Sony)'}
                                    icon={order?.supplier_mode === 'none' ? Truck : Package}
                                    iconColor={order?.supplier_mode === 'none' ? 'text-slate-400' : 'text-amber-600'}
                                    status={order?.supplier_mode === 'none' ? 'disabled' :
                                           order?.supplier_confirmation?.delivery_notice_sent_at ? 'complete' :
                                           order?.supplier_confirmation?.dispatch_delivery_note_number ? 'dispatched' :
                                           order?.supplier_confirmation?.status === 'Approved' ? 'approved' : 
                                           order?.supplier_confirmation?.status === 'Mismatch' ? 'attention' :
                                           order?.supplier_confirmation?.po_sent_at ? 'pending' :
                                           order?.supplier_confirmation?.sony_leos_number ? 'in_progress' :
                                           order?.supplier_confirmation ? 'in_progress' : 'not_started'}
                                    statusLabel={order?.supplier_mode === 'none' ? 'Direct Production' :
                                                order?.supplier_confirmation?.delivery_notice_sent_at ? 'Dispatched' :
                                                order?.supplier_confirmation?.dispatch_delivery_note_number ? 'Dispatched' :
                                                order?.supplier_confirmation?.status === 'Approved' ? 'Confirmed' :
                                                order?.supplier_confirmation?.status === 'Mismatch' ? 'Mismatch' :
                                                order?.supplier_confirmation?.po_sent_at ? 'PO Sent' :
                                                order?.supplier_confirmation?.sony_leos_number ? 'PO In Progress' :
                                                order?.supplier_confirmation ? 'In Progress' : 'Not started'}
                                    attentionRequired={order?.supplier_confirmation?.status === 'Mismatch'}
                                    nextActionOwner={
                                        order?.supplier_mode === 'none' ? null :
                                        order?.supplier_confirmation?.status === 'Mismatch' ? 'admin' :
                                        (order?.supplier_confirmation?.po_sent_at
                                         && !order?.supplier_confirmation?.approved_at
                                         && !order?.supplier_confirmation?.inbound_received_at
                                         && !order?.supplier_confirmation?.quantity_confirmed)
                                            ? 'supplier' : null
                                    }
                                    defaultOpen={false}
                                    summary={order?.supplier_mode === 'none' 
                                        ? 'No supplier workflow - direct invoicing available' 
                                        : order?.supplier_confirmation?.supplier 
                                            ? `${order.supplier_confirmation.supplier} • PO → Confirmation → Dispatch` 
                                            : 'Track supplier workflow'}
                                >
                                    {/* Supplier Mode Toggle */}
                                    <div className="flex items-center justify-between p-3 bg-background rounded-lg border mb-4">
                                        <div className="flex items-center gap-3">
                                            <Switch
                                                id="supplier-mode-toggle"
                                                checked={order?.supplier_mode !== 'none'}
                                                onCheckedChange={handleToggleSupplierMode}
                                                disabled={isTogglingSupplierMode || (order?.supplier_confirmation?.po_sent_at)}
                                                data-testid="supplier-mode-toggle"
                                            />
                                            <div>
                                                <Label htmlFor="supplier-mode-toggle" className="text-sm font-medium cursor-pointer">
                                                    Use supplier workflow
                                                </Label>
                                                <p className="text-xs text-slate-500">
                                                    {order?.supplier_mode === 'none' 
                                                        ? 'Direct production without supplier PO/confirmation' 
                                                        : 'Sony DADC as default supplier'}
                                                </p>
                                            </div>
                                        </div>
                                        {order?.supplier_confirmation?.po_sent_at && (
                                            <span className="text-xs text-amber-600 flex items-center gap-1">
                                                <AlertTriangle className="h-3 w-3" />
                                                PO sent - cannot change
                                            </span>
                                        )}
                                    </div>
                                    
                                    {/* Show supplier workflow content only when enabled */}
                                    {order?.supplier_mode !== 'none' ? (
                                        <Tabs defaultValue="production" className="mt-2" data-testid="supplier-workflow-tabs">
                                            <TabsList className="grid grid-cols-2 w-full max-w-md mb-3">
                                                <TabsTrigger value="production" data-testid="supplier-tab-production">
                                                    Production Order
                                                </TabsTrigger>
                                                <TabsTrigger value="test_press" data-testid="supplier-tab-test-press">
                                                    Test Press
                                                </TabsTrigger>
                                            </TabsList>
                                            <TabsContent value="production">
                                                <SupplierConfirmationSection
                                                    order={order}
                                                    onUpdate={fetchData}
                                                    embedded={true}
                                                    variant="production"
                                                    isAdmin={isAdmin}
                                                />
                                            </TabsContent>
                                            <TabsContent value="test_press">
                                                <div className="text-[11px] text-slate-500 mb-2 px-1">
                                                    Test Press workflow — runs in parallel to the production order.
                                                    Test-press confirmations and dispatches are hermetically isolated and
                                                    never overwrite production data. PO references get the <code>-TP</code> suffix.
                                                </div>
                                                <TestPressSupplierHistory order={order} />
                                                <SupplierConfirmationSection
                                                    order={order}
                                                    onUpdate={fetchData}
                                                    embedded={true}
                                                    variant="test_press"
                                                    isAdmin={isAdmin}
                                                />
                                            </TabsContent>
                                        </Tabs>
                                    ) : (
                                        <div className="text-center py-6 text-slate-500">
                                            <Truck className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                                            <p className="text-sm font-medium">Supplier workflow disabled</p>
                                            <p className="text-xs mt-1">Invoice can be created directly from the Finance section</p>
                                        </div>
                                    )}
                                </AdminCollapsibleSection>
                            )}

                            {/* 5. Finance */}
                            {isAdmin && (() => {
                                // Authoritative invoice state — mirror the same
                                // rule used by the Sales Document card and the
                                // orders list "Invoiced" badge, so this header
                                // never drifts out of sync with them.
                                const inv = order?.invoice || null;
                                const isBooked = !!inv?.economic_booked
                                    || inv?.economic_status === 'booked_in_economic'
                                    || order?.invoice_economic_status === 'booked_in_economic'
                                    || ['booked', 'paid', 'invoiced'].includes((order?.invoice_status || inv?.status || '').toLowerCase());
                                const isEconomicDraft = !isBooked && (
                                    inv?.economic_status === 'draft_in_economic'
                                    || order?.invoice_economic_status === 'draft_in_economic'
                                );

                                let headerStatus;
                                let headerStatusLabel;
                                let headerSummary;
                                if (isBooked) {
                                    headerStatus = 'invoiced';
                                    headerStatusLabel = 'Invoiced';
                                    headerSummary = 'Booked in e-conomic';
                                } else if (isEconomicDraft) {
                                    headerStatus = 'ready_to_invoice';
                                    headerStatusLabel = 'Draft in e-conomic';
                                    headerSummary = 'Draft sent to e-conomic — awaiting booking';
                                } else if (order?.supplier_mode === 'none') {
                                    headerStatus = 'ready_to_invoice';
                                    headerStatusLabel = 'Direct Invoicing';
                                    headerSummary = 'No supplier workflow → Direct invoicing';
                                } else if (order?.supplier_confirmation?.delivery_notice_sent_at) {
                                    headerStatus = 'ready_to_invoice';
                                    headerStatusLabel = 'Ready to Invoice';
                                    headerSummary = 'Purchase → Dispatch → Delivery → Invoice';
                                } else if (order?.supplier_confirmation?.dispatch_delivery_note_number) {
                                    headerStatus = 'dispatched';
                                    headerStatusLabel = 'Dispatched';
                                    headerSummary = 'Purchase → Dispatch → Delivery → Invoice';
                                } else {
                                    headerStatus = 'tracking';
                                    headerStatusLabel = 'Tracking';
                                    headerSummary = 'Purchase → Dispatch → Delivery → Invoice';
                                }

                                // Quote state badge — surfaced on the
                                // collapsed Finance header so admins can see
                                // "Quote Requested" / "Quote Sent" /
                                // "Quote Approved" without expanding the
                                // accordion. Pure presentation — no state
                                // change. Hidden when the order has never
                                // been through the quote workflow.
                                let quoteSecondaryStatus = null;
                                let quoteSecondaryLabel = null;
                                const _qvCount = Array.isArray(order?.quote_versions) ? order.quote_versions.length : 0;
                                if (order?.status === 'Quote') {
                                    quoteSecondaryStatus = 'pending';
                                    quoteSecondaryLabel = 'Quote Requested';
                                } else if (order?.status === 'Quoted') {
                                    quoteSecondaryStatus = 'sent';
                                    quoteSecondaryLabel = 'Quote Sent';
                                } else if (_qvCount > 0) {
                                    // Order left the quote flow (Submitted
                                    // or later) with a sent/approved
                                    // version on file → treat as approved.
                                    quoteSecondaryStatus = 'approved';
                                    quoteSecondaryLabel = 'Quote Approved';
                                }

                                // Supplier-RFQ state — surfaced ALONGSIDE
                                // the customer-quote state (not instead
                                // of it). The two lifecycles are
                                // independent: the admin can be waiting
                                // on a supplier quote while the customer
                                // has already approved. Keep both bits
                                // visible on the collapsed header by
                                // concatenating the labels. If both are
                                // present the RFQ half shows first so
                                // "something internal is pending" sticks
                                // out visually.
                                const _rfq = deriveRfqStatus(order);
                                let rfqHeaderPart = null;
                                let rfqHeaderStatus = null;
                                if (_rfq.state === RFQ_STATE.RECEIVED) {
                                    rfqHeaderPart = 'Supplier Quote Received';
                                    rfqHeaderStatus = 'approved';
                                } else if (_rfq.state === RFQ_STATE.AWAITING_SUPPLIER_QUOTE) {
                                    rfqHeaderPart = 'Awaiting Supplier Quote';
                                    rfqHeaderStatus = 'pending';
                                }
                                if (rfqHeaderPart) {
                                    if (quoteSecondaryLabel) {
                                        quoteSecondaryLabel = `${rfqHeaderPart} · ${quoteSecondaryLabel}`;
                                    } else {
                                        quoteSecondaryStatus = rfqHeaderStatus;
                                        quoteSecondaryLabel = rfqHeaderPart;
                                    }
                                }

                                return (
                                    <AdminCollapsibleSection
                                        id="finance"
                                        title="Finance"
                                        icon={DollarSign}
                                        iconColor="text-emerald-600"
                                        status={headerStatus}
                                        statusLabel={headerStatusLabel}
                                        secondaryStatus={quoteSecondaryStatus}
                                        secondaryStatusLabel={quoteSecondaryLabel}
                                        defaultOpen={false}
                                        summary={headerSummary}
                                        data-testid="finance-accordion"
                                    >
                                        <FinanceSection
                                            order={order}
                                            onUpdate={fetchData}
                                            embedded={true}
                                            artworkJob={artworkJob}
                                        />
                                    </AdminCollapsibleSection>
                                );
                            })()}

                            {/* 6. S3 Files */}
                            {isAdmin && adminFiles.length > 0 && (
                                <AdminCollapsibleSection
                                    id="s3-files"
                                    title="S3 Files"
                                    icon={FolderOpen}
                                    iconColor="text-blue-600"
                                    status="info"
                                    statusLabel={`${adminFiles.length} file${adminFiles.length !== 1 ? 's' : ''}`}
                                    defaultOpen={false}
                                    summary={`View uploaded files with S3 paths`}
                                >
                                    <div className="space-y-2">
                                        <p className="text-xs text-slate-500 mb-3">
                                            All uploaded files with full S3 bucket paths. Download fetches a fresh signed URL each click — links never go stale.
                                        </p>
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead className="text-xs">File</TableHead>
                                                    <TableHead className="text-xs">Type</TableHead>
                                                    <TableHead className="text-xs">Size</TableHead>
                                                    <TableHead className="text-xs">S3 Path</TableHead>
                                                    <TableHead className="text-xs w-20 text-right">Actions</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {adminFiles.map((file) => (
                                                    <TableRow key={file.id} data-testid={`admin-file-row-${file.id}`}>
                                                        <TableCell className="text-xs font-medium">
                                                            <div className="flex items-center gap-2">
                                                                {file.file_type === 'audio' || (file.file_type || '').startsWith('audio_') || (file.file_type || '').startsWith('master_slot_') ? (
                                                                    <Music className="w-3.5 h-3.5 text-purple-500" />
                                                                ) : file.file_type === 'artwork' ? (
                                                                    <Image className="w-3.5 h-3.5 text-pink-500" />
                                                                ) : (
                                                                    <FileText className="w-3.5 h-3.5 text-slate-500" />
                                                                )}
                                                                <span className="truncate max-w-[180px]" title={file.original_filename}>
                                                                    {file.original_filename}
                                                                </span>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="text-xs text-slate-500">
                                                            <div className="flex items-center gap-1.5">
                                                                <span className="truncate inline-block max-w-[140px]" title={file.file_type}>
                                                                    {file.file_type}
                                                                </span>
                                                                {file.source === 's3' && (
                                                                    <span
                                                                        className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold bg-amber-100 text-amber-800 border border-amber-300"
                                                                        title="Object exists in S3 but has no database record (legacy or out-of-band upload)"
                                                                        data-testid={`file-source-s3-${file.id}`}
                                                                    >
                                                                        S3 ONLY
                                                                    </span>
                                                                )}
                                                                {file.source === 'db' && (
                                                                    <span
                                                                        className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold bg-red-100 text-red-800 border border-red-300"
                                                                        title="Database record exists but the S3 object is missing"
                                                                        data-testid={`file-source-db-${file.id}`}
                                                                    >
                                                                        DB ONLY
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                                                            {file.size > 1073741824
                                                                ? `${(file.size / 1073741824).toFixed(2)} GB`
                                                                : file.size > 1048576
                                                                    ? `${(file.size / 1048576).toFixed(1)} MB`
                                                                    : file.size > 1024
                                                                        ? `${(file.size / 1024).toFixed(1)} KB`
                                                                        : `${file.size} B`
                                                            }
                                                        </TableCell>
                                                        <TableCell className="text-xs">
                                                            <code className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded text-slate-600 block truncate max-w-[200px]" title={file.s3_path}>
                                                                {file.s3_path || 'N/A'}
                                                            </code>
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex items-center justify-end gap-0.5">
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    className="h-7 w-7 p-0"
                                                                    title="Download (signed S3 URL)"
                                                                    onClick={() => handleDownloadFile(file)}
                                                                    data-testid={`admin-file-download-${file.id}`}
                                                                >
                                                                    <Download className="w-3.5 h-3.5 text-blue-500" />
                                                                </Button>
                                                                {file.s3_path && (
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        className="h-7 w-7 p-0"
                                                                        title="Copy S3 path"
                                                                        onClick={() => {
                                                                            navigator.clipboard.writeText(file.s3_path);
                                                                            setCopiedPath(file.id);
                                                                            toast.success('S3 path copied to clipboard');
                                                                            setTimeout(() => setCopiedPath(null), 2000);
                                                                        }}
                                                                        data-testid={`admin-file-copy-${file.id}`}
                                                                    >
                                                                        {copiedPath === file.id ? (
                                                                            <Check className="w-3.5 h-3.5 text-green-500" />
                                                                        ) : (
                                                                            <Copy className="w-3.5 h-3.5 text-slate-400" />
                                                                        )}
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </AdminCollapsibleSection>
                            )}

                            {/* 7. Upload Links */}
                            {isAdmin && (
                                <AdminCollapsibleSection
                                    id="upload-links"
                                    title="Upload Links"
                                    icon={Link}
                                    iconColor="text-violet-600"
                                    status="info"
                                    statusLabel="Share"
                                    defaultOpen={false}
                                    summary="Create secure links for external partners to upload files"
                                >
                                    <UploadLinksSection orderId={orderId} />
                                </AdminCollapsibleSection>
                            )}

                            {/* 8. System Events — technical timeline for this order */}
                            <OrderSystemEvents orderId={orderId} />
                        </CollapsibleSectionsProvider>
                    </div>

                    {/* RIGHT PANEL - History & Status */}
                    <div className="col-span-12 lg:col-span-2">
                        <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 px-1 mb-3">Files</div>
                        
                        {/* Linked Artwork (Quick Reference) */}
                        {artworkFiles.length > 0 && (
                            <div className="bg-white rounded-lg border border-slate-300 shadow-premium-sm p-3 mb-3">
                                <div className="text-xs font-medium uppercase tracking-wider text-slate-600 flex items-center gap-2 mb-2">
                                    <Image className="h-3.5 w-3.5" />
                                    Artwork Files
                                    <span className="text-slate-400">({artworkFiles.length})</span>
                                </div>
                                <div className="space-y-1">
                                    {artworkFiles.slice(0, 5).map(file => (
                                        <div key={file.id} className="flex items-center gap-2 py-1 text-xs">
                                            <div className="w-8 h-8 bg-slate-100 rounded flex items-center justify-center flex-shrink-0">
                                                <Image className="h-4 w-4 text-slate-400" />
                                            </div>
                                            <span className="truncate text-slate-600 flex-1">{file.original_filename}</span>
                                        </div>
                                    ))}
                                    {artworkFiles.length > 5 && (
                                        <div className="text-[10px] text-slate-400 pl-10">
                                            +{artworkFiles.length - 5} more
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Inbound Documents - Admin only */}
                        {isAdmin && (
                            <div className="bg-white rounded-lg border border-slate-300 shadow-premium-sm p-3 mb-3">
                                <div className="text-xs font-medium uppercase tracking-wider text-slate-600 flex items-center gap-2 mb-2">
                                    <Inbox className="h-3.5 w-3.5" />
                                    Inbound Documents
                                </div>
                                <InboundDocumentsSection orderId={order?.id} />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Edit Purchase Dialog */}
            <Dialog open={isEditPurchaseDialogOpen} onOpenChange={setIsEditPurchaseDialogOpen}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Edit Purchase</DialogTitle>
                        {selectedPurchase && isFinancialLocked(selectedPurchase.status) && (
                            <DialogDescription className="text-amber-600">
                                Financial fields locked (status: {selectedPurchase.status})
                            </DialogDescription>
                        )}
                    </DialogHeader>
                    <form onSubmit={handleEditPurchase} className="space-y-3 mt-2">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs">Type</Label>
                                <Select 
                                    value={editPurchaseData.type} 
                                    onValueChange={(v) => setEditPurchaseData({...editPurchaseData, type: v})}
                                    disabled={selectedPurchase && isFinancialLocked(selectedPurchase.status)}
                                >
                                    <SelectTrigger className="h-8 text-sm">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {purchaseTypes.map((type) => (
                                            <SelectItem key={type} value={type}>{type}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs">Status</Label>
                                <Select value={editPurchaseData.status} onValueChange={(v) => setEditPurchaseData({...editPurchaseData, status: v})}>
                                    <SelectTrigger className="h-8 text-sm">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {purchaseStatuses.map((status) => (
                                            <SelectItem key={status} value={status}>{status}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs">Description</Label>
                            <Textarea
                                value={editPurchaseData.description}
                                onChange={(e) => setEditPurchaseData({...editPurchaseData, description: e.target.value})}
                                className="text-sm min-h-[60px]"
                            />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div>
                                <Label className="text-xs">Amount</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={editPurchaseData.amount}
                                    onChange={(e) => setEditPurchaseData({...editPurchaseData, amount: e.target.value})}
                                    className="h-8 text-sm"
                                    disabled={selectedPurchase && isFinancialLocked(selectedPurchase.status)}
                                />
                            </div>
                            <div>
                                <Label className="text-xs">Currency</Label>
                                <Select 
                                    value={editPurchaseData.currency} 
                                    onValueChange={(v) => setEditPurchaseData({...editPurchaseData, currency: v})}
                                    disabled={selectedPurchase && isFinancialLocked(selectedPurchase.status)}
                                >
                                    <SelectTrigger className="h-8 text-sm">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="DKK">DKK</SelectItem>
                                        <SelectItem value="EUR">EUR</SelectItem>
                                        <SelectItem value="USD">USD</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs">Ref</Label>
                                <Input
                                    value={editPurchaseData.supplier_reference}
                                    onChange={(e) => setEditPurchaseData({...editPurchaseData, supplier_reference: e.target.value})}
                                    className="h-8 text-sm"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <Button type="button" variant="outline" size="sm" onClick={() => setIsEditPurchaseDialogOpen(false)}>Cancel</Button>
                            <Button type="submit" size="sm" disabled={isSubmitting}>
                                {isSubmitting ? 'Saving...' : 'Save'}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Create Artwork Job Dialog */}
            <Dialog open={isCreateArtworkJobDialogOpen} onOpenChange={setIsCreateArtworkJobDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Palette className="h-5 w-5 text-purple-500" />
                            Create Artwork Job
                        </DialogTitle>
                        <DialogDescription>
                            Create a new artwork correction job and assign it to a partner.
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Artwork Partner *</Label>
                            <Select 
                                value={artworkJobForm.partner_id} 
                                onValueChange={(v) => setArtworkJobForm({...artworkJobForm, partner_id: v})}
                            >
                                <SelectTrigger data-testid="artwork-job-partner-select">
                                    <SelectValue placeholder="Select partner..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {reproPartners.map((partner) => (
                                        <SelectItem key={partner.id} value={partner.id}>
                                            <div className="flex items-center gap-2">
                                                <span>{partner.name}</span>
                                                {partner.is_default && (
                                                    <Star className="h-3 w-3 text-amber-500" />
                                                )}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Instructions (optional)</Label>
                            <Textarea
                                value={artworkJobForm.instructions}
                                onChange={(e) => setArtworkJobForm({...artworkJobForm, instructions: e.target.value})}
                                placeholder="Special instructions for the artwork partner..."
                                rows={3}
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsCreateArtworkJobDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleCreateArtworkJob}
                            disabled={!artworkJobForm.partner_id || isCreatingArtworkJob}
                            data-testid="confirm-create-artwork-job-btn"
                        >
                            {isCreatingArtworkJob ? 'Creating...' : 'Create & Assign'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Admin: Manual Quantity Override Dialog */}
            <Dialog open={isQuantityDialogOpen} onOpenChange={setIsQuantityDialogOpen}>
                <DialogContent className="sm:max-w-md" data-testid="admin-quantity-dialog">
                    <DialogHeader>
                        <DialogTitle>Update Order Quantity</DialogTitle>
                        <DialogDescription>
                            Admin-only override for submitted orders. Customers must call to request a quantity change — they cannot do this themselves.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-2">
                        <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 flex items-start gap-2">
                            <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-amber-800">
                                Changing quantity on a submitted order can affect downstream purchasing and invoicing. Choose carefully which records to sync.
                            </p>
                        </div>

                        <div className="space-y-1.5">
                            <Label htmlFor="admin-new-quantity">New Quantity</Label>
                            <div className="flex items-center gap-3">
                                <span className="text-xs text-slate-500 whitespace-nowrap">
                                    Current: <strong className="text-slate-700">{vinylSpecs?.quantity || order?.quantity || 0}</strong>
                                </span>
                                <Input
                                    id="admin-new-quantity"
                                    type="number"
                                    min="1"
                                    step="1"
                                    value={quantityForm.new_quantity}
                                    onChange={(e) => setQuantityForm({ ...quantityForm, new_quantity: e.target.value })}
                                    placeholder="Enter new quantity"
                                    data-testid="admin-new-quantity-input"
                                />
                            </div>
                        </div>

                        <div className="space-y-3 pt-1">
                            <p className="text-xs font-medium text-slate-700">Should the new quantity also update:</p>

                            <label className="flex items-start gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={quantityForm.propagate_purchases}
                                    onChange={(e) => setQuantityForm({ ...quantityForm, propagate_purchases: e.target.checked })}
                                    className="mt-0.5 h-4 w-4 rounded border-slate-300"
                                    data-testid="admin-propagate-purchases-checkbox"
                                />
                                <div className="flex-1">
                                    <span className="text-sm text-slate-800 font-medium">Purchasing (PO lines)</span>
                                    <p className="text-xs text-slate-500">Re-scale all PO lines flagged as scaling with order quantity.</p>
                                </div>
                            </label>

                            <label className="flex items-start gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={quantityForm.propagate_invoice}
                                    onChange={(e) => setQuantityForm({ ...quantityForm, propagate_invoice: e.target.checked })}
                                    className="mt-0.5 h-4 w-4 rounded border-slate-300"
                                    data-testid="admin-propagate-invoice-checkbox"
                                />
                                <div className="flex-1">
                                    <span className="text-sm text-slate-800 font-medium">Sales order / Invoice basis</span>
                                    <p className="text-xs text-slate-500">Re-scale invoice lines tied to order quantity and recompute totals.</p>
                                </div>
                            </label>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setIsQuantityDialogOpen(false)}
                            disabled={isSavingQuantity}
                            data-testid="admin-quantity-cancel-btn"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleAdminUpdateQuantity}
                            disabled={isSavingQuantity || !quantityForm.new_quantity}
                            data-testid="admin-quantity-save-btn"
                        >
                            {isSavingQuantity ? 'Updating...' : 'Update Quantity'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <RejectMasterFileDialog
                open={!!rejectingMasterFile}
                onOpenChange={(open) => { if (!open) setRejectingMasterFile(null); }}
                orderId={order?.id}
                file={rejectingMasterFile}
                onRejected={(updatedFile) => {
                    setRejectingMasterFile(null);
                    setFiles((prev) =>
                        prev.map((f) => (f.id === updatedFile.id ? { ...f, ...updatedFile } : f)),
                    );
                }}
            />

            {/* ── Confirmation dialog for "Generate missing MD5 files"
                (per-file .md5 sidecar reconciliation). Lists exactly
                which master files will get a FLOW-generated sidecar,
                which are already covered, and which are still waiting
                for their hash. */}
            <Dialog
                open={!!md5SidecarPlan}
                onOpenChange={(open) => {
                    if (!open) {
                        setMd5SidecarPlan(null);
                        setIsGeneratingMd5Sidecars(false);
                    }
                }}
            >
                <DialogContent className="max-w-2xl" data-testid="md5-sidecar-plan-dialog">
                    <DialogHeader>
                        <DialogTitle>Generate missing MD5 files</DialogTitle>
                        <DialogDescription>
                            One <code>.md5</code> sidecar file will be written to S3 next
                            to each listed master, using the trusted, server-computed hash
                            already stored by FLOW. Files where a customer-supplied MD5
                            already covers the master are skipped. Existing Sony jobs are
                            not modified.
                        </DialogDescription>
                    </DialogHeader>
                    {md5SidecarPlan && (
                        <div className="space-y-4 text-sm">
                            <section data-testid="md5-plan-to-generate">
                                <h4 className="font-medium text-slate-900 dark:text-slate-100 mb-1.5">
                                    Will generate ({md5SidecarPlan.to_generate?.length || 0})
                                </h4>
                                {(md5SidecarPlan.to_generate || []).length === 0 ? (
                                    <p className="text-xs text-slate-500">Nothing new to generate — every eligible master is already covered.</p>
                                ) : (
                                    <ul className="text-xs font-mono space-y-1 max-h-40 overflow-auto border rounded p-2 bg-slate-50 dark:bg-slate-900">
                                        {md5SidecarPlan.to_generate.map(f => (
                                            <li key={f.file_id} className="flex items-center justify-between gap-2">
                                                <span className="truncate">{f.sidecar_filename}</span>
                                                <span className="text-slate-400 shrink-0">{(f.md5 || '').slice(0, 8)}…</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </section>
                            {(md5SidecarPlan.already_covered || []).length > 0 && (
                                <section data-testid="md5-plan-already-covered">
                                    <h4 className="font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                                        Already covered ({md5SidecarPlan.already_covered.length})
                                    </h4>
                                    <ul className="text-xs text-slate-500 space-y-0.5 max-h-24 overflow-auto">
                                        {md5SidecarPlan.already_covered.map(f => (
                                            <li key={f.file_id}>
                                                <span className="font-mono">{f.filename}</span>
                                                <span className="ml-2 text-[10px] uppercase tracking-wide">{f.reason.replace(/-/g, ' ')}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </section>
                            )}
                            {(md5SidecarPlan.waiting_for_hash || []).length > 0 && (
                                <section data-testid="md5-plan-waiting">
                                    <h4 className="font-medium text-amber-700 dark:text-amber-400 mb-1.5">
                                        Waiting for hash — run &ldquo;Generate missing MD5&rdquo; first ({md5SidecarPlan.waiting_for_hash.length})
                                    </h4>
                                    <ul className="text-xs text-amber-700 dark:text-amber-400 space-y-0.5 max-h-24 overflow-auto font-mono">
                                        {md5SidecarPlan.waiting_for_hash.map(f => (
                                            <li key={f.file_id}>{f.filename}</li>
                                        ))}
                                    </ul>
                                </section>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => { setMd5SidecarPlan(null); setIsGeneratingMd5Sidecars(false); }}
                            data-testid="md5-sidecar-plan-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            disabled={(md5SidecarPlan?.to_generate?.length || 0) === 0 || isGeneratingMd5Sidecars}
                            onClick={async () => {
                                if (isGeneratingMd5Sidecars) return;
                                setIsGeneratingMd5Sidecars(true);
                                try {
                                    const res = await filesApi.generateMd5Sidecars(orderId);
                                    const s = res.data || {};
                                    const parts = [];
                                    if ((s.generated || []).length) parts.push(`${s.generated.length} sidecar(s) generated`);
                                    if ((s.refreshed || []).length) parts.push(`${s.refreshed.length} refreshed`);
                                    if ((s.removed || []).length) parts.push(`${s.removed.length} removed`);
                                    if ((s.skipped_customer_md5 || []).length) parts.push(`${s.skipped_customer_md5.length} skipped (customer md5)`);
                                    toast.success(parts.length ? parts.join(' · ') : 'No changes', { duration: 8000 });
                                    const fresh = await ordersApi.getFiles(orderId);
                                    setFiles(fresh.data || []);
                                } catch (err) {
                                    toast.error(err.response?.data?.detail || 'Sidecar generation failed');
                                } finally {
                                    setMd5SidecarPlan(null);
                                    setIsGeneratingMd5Sidecars(false);
                                }
                            }}
                            data-testid="md5-sidecar-plan-confirm"
                        >
                            {isGeneratingMd5Sidecars ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                            Generate {md5SidecarPlan?.to_generate?.length || 0} file(s)
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
